import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { logAnalyticsEvent, isNetworkShapedError } from '../lib/api';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { sortLibraryDocs } from '../lib/sortLibraryDocs';
// t7-1b (T5): las cards que el profe del grupo publicó, para estudiarlas aquí.
import { cargarMaterialDelGrupo } from '../lib/materialDelGrupo';
import { BookOpen, Zap, Target, FileText, Play, Info, ChevronDown, ChevronUp, CloudOff, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { useScreenData } from '../hooks/useScreenData';
import { studyAvailability, STUDY_UNAVAILABLE_REASON, examRunsLocally } from '../lib/brain';
// pantallas-alumno (C): el esqueleto que copia el acomodo nuevo, y las cajas que comparte con el.
import EsqueletoDelCentro from '../components/centro/EsqueletoDelCentro';
import {
  TITULO_DEL_CENTRO, RECUADRO_DEL_CENTRO, PAGINA_DEL_CENTRO, REJILLA_DEL_CENTRO, CAJA_DE_LA_LISTA,
  PEGADO_EN_COMPU, TITULO_DEL_RECUADRO, LISTA_DEL_CENTRO, DEGRADADO_DE_LA_LISTA, DOCS_ELEGIDOS,
  RECUADRO_DE_DOCUMENTOS, SETTINGS_DEL_CENTRO, CUERPO_DE_SETTINGS, PIE_DE_SETTINGS, AIRE_SOBRE_LA_BARRA, APRETADO_EN_EXAM,
} from '../components/centro/acomodoDelCentro';
// pantallas-alumno (C): las piezas comunes del alumno (ayudante B) y las del Panel que se importan.
import {
  Boton, ChipVerde, TarjetaDeModo, FilaDeModos, RenglonDeDocumento, RecuadroDeLoQueFalta, CajaDeNumero,
  BarraDeslizante, BotonDeTemas, BarraFijaDeAcciones, RecuadroDeEstado, avisarSinRed,
} from '../components/alumno';
import ChipDeEstado from '../components/panel/ChipDeEstado';
import { claveDelTipo } from '../components/panel/tipoDelDocumento';
import { AreaDe44, LIGA_RENGLON } from '../components/panel/anatomia';
// firma 1 de la parte 1: los rechazos de "Generate Missing Questions" dan el aviso que hoy da el
// Retry del Panel (Dashboard.js, `_retryErrorToast`), con la MISMA funcion.
import { retryErrorToast } from '../lib/retryErrorToast';
// TANDA 22 (#213): esta pantalla dejó de importar el colchón compartido — ahora
// escribe el suyo, recortado en móvil. La constante sigue exportada e intacta
// para AdaptiveStudy y ExamSimulator, que la usan tal cual.
// pantallas-alumno (C): la barra fija es la pieza del alumno (`BarraFijaDeAcciones`), con el mismo
// anclaje de siempre: a la pantalla, o a la caja de su hoja dentro del carril.
import { ANCLAJE } from '../components/SessionActionBar';
// TANDA 29 (#272) — el selector de temas de la sesión.
import TopicPickerModal from '../components/TopicPickerModal';
// F6.7b — la cara confiable: red reactiva, presencia local viva, disponibilidad
// POR MODO y el candado Pro del estudio sin conexión.
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useLocalPresence } from '../hooks/useLocalPresence';
import { isExplicitlyOffline, isOfflineForGate } from '../lib/offline/networkVerdict';
import { selectionBlockedByMaterial } from '../lib/offline/deliveryState';
import { evaluateOfflineProGate, GATE_VARIANT } from '../lib/offline/proGate';
import OfflineProModal from '../components/OfflineProModal';
import { log as diagLog } from '../lib/diag/readyChainLog';
import { useReportarCapa, useMarcoDeLaPane, useScrollportRaiz } from '../lib/pager/pane';
// PAGER-1 — el contrato montada/activa (la doctrina completa vive en el módulo).
import { useRaizActiva } from '../lib/pager/activacion';
// post-t7-1 M4: el colchon de abajo crece con los renglones de mas del aviso de la barra
import { useExtraDelAviso, COLCHON_DEL_CENTRO_PX } from '../lib/extraDelAviso';

// FLASH-3 (pieza 1) — LA FIRMA DEL EMPUJÓN. Hermana de `NAV.REDIRECT`
// (ExamSimulator.js) y de `NAV.GUARD` (ExamLockedRoute): las dos cubren
// navegaciones que la app decide sola, y faltaba la que el USUARIO dispara. Con
// el token dentro, un doble disparo futuro queda FIRMADO: dos líneas `NAV.PUSH`
// con tokens distintos son dos taps; con el mismo token, un solo tap repintado.
// Mismo contrato que los demás escritores: no-op total en web, jamás lanza,
// cero timers.
function logNavPush(hacia, token) {
  if (!IS_APP_BUILD) return;
  try {
    diagLog('NAV.PUSH', { desde: 'study-center', hacia, token: token ?? null });
  } catch (e) { /* el visor jamás decide una navegación */ }
}

// Visual-only mode config. Display name + description come from study:center.modes.*
// pantallas-alumno (C, "Decisiones de esta version" de la parte 1): un solo naranja para los tres
// modos (lo pinta la tarjeta de modo); hoy cada uno tenia su color.
const MODES_VISUAL = [
  { id: 'adaptive', icon: Target },
  { id: 'exam', icon: BookOpen },
  { id: 'flashcards', icon: Zap },
];

/* pantallas-alumno (C): la holgura de abajo en el celular, para que la barra fija no tape lo ultimo
   (la barra mide 12 + 18 + 8 + 52 + 12 + 1 = 103; se reservan 120, `COLCHON_DEL_CENTRO_PX`). */
const HOLGURA_DEL_CENTRO = 'pb-[120px] md:pb-0';

/* T1-C1 y T2-C4: los dos cuadros de numero del examen RECUPERAN la seleccion de texto dentro de la
   pantalla sin seleccion de la app (seleccionar todo al enfocar y el menu de pegar viven de eso). Va en
   linea porque la caja es la pieza del alumno: una declaracion directa le gana a la heredada. */
const SELECCION_DE_TEXTO = { userSelect: 'text', WebkitUserSelect: 'text', WebkitTouchCallout: 'default' };

// Dato de pantalla completo en un solo fetcher (cacheado bajo 'study-center',
// regla de frescura F4.3b). El historial es best-effort como siempre: su falla
// no tumba la pantalla, solo deja el badge "Recomendado" en su default.
//
// F6.4b: si el fetch vivo cae con forma-de-red en el build nativo, el MISMO
// shape se resuelve desde la bodega (ensamblador de screenSources, import
// dinámico que la web jamás pide) — el dato entra por el camino normal del
// fetcher, jamás como segundo render encima de otro (stale-then-swap
// prohibido). `offline: true` en el resultado es lo que deshabilita el CTA.
/* pantallas-alumno (C, P8): los documentos bloqueados por cambio de plan, en el orden de la
   biblioteca. Se pintan DEBAJO de los que se estudian, con candado y apagados. En la app sin red la
   lista sale de la bodega, que no los trae (`locked_at IS NULL`, lib/warehouse/screenSources.js):
   ahi siguen sin salir, como hoy. */
export function bloqueadosDe(documentos) {
  return sortLibraryDocs((Array.isArray(documentos) ? documentos : []).filter((d) => d && d.status === 'archived'));
}

export async function fetchStudyCenterData(userId) {
  // F6.7b (fase 2) — ATAJO OFFLINE. Con DICTAMEN explícito del vigía (evento
  // `offline` recibido, jamás sospecha) las dos peticiones de abajo están
  // condenadas y axios no fija timeout propio (api.js:18), así que en modo
  // avión el usuario se come el timeout del sistema mirando el esqueleto. Aquí
  // la bodega responde de inmediato. Con red no cambia NADA: el camino vivo
  // corre exactamente como hoy y local-primero no cancela nada.
  if (IS_APP_BUILD && isExplicitlyOffline()) {
    try {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.studyCenterFromWarehouse(userId);
      if (fromWarehouse) return fromWarehouse;
    } catch (e) { /* sin bodega utilizable → camino normal de abajo */ }
  }
  try {
    const [docsRes, historyRes] = await Promise.all([
      api.get('/documents'),
      api.get('/study/history').catch(() => ({ data: { sessions: [] } })),
    ]);
    const docs = sortLibraryDocs(docsRes.data.documents.filter(d => d.status === 'ready' || d.status === 'partial'));
    const completedSessions = (historyRes.data.sessions || []).filter(s => s.status === 'completed').length;
    // pantallas-alumno (C, P8): los bloqueados por cambio de plan (`archived`, la unica razon de
    // archivo es la bajada de plan: services/document_archive.py) vienen en la MISMA lista; van
    // aparte para que nada de lo que estudia (conteos, seleccion, `?doc=`) los vea.
    const bloqueados = bloqueadosDe(docsRes.data.documents);
    return { docs, completedSessions, bloqueados };
  } catch (err) {
    // Solo el fetch PRINCIPAL (documents) llega aquí — el history conserva su
    // best-effort de siempre. Camino vivo exitoso: idéntico a hoy.
    if (IS_APP_BUILD && isNetworkShapedError(err)) {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.studyCenterFromWarehouse(userId);
      if (fromWarehouse) return fromWarehouse;
    }
    throw err;
  }
}

// F6.8 T2 (H8) — EL RESUMEN DE FLASHCARDS GANA EL PATRÓN DE SUS HERMANOS.
//
// Hasta aquí éste era el ÚNICO número derivado de estudio de la pantalla sin
// ningún camino local: sin señal el `catch` lo dejaba en `null` y el desglose
// "conocidas / aún aprendiendo" DESAPARECÍA EN SILENCIO (censo §E.1). Ahora
// lleva las dos puertas que ya tiene el fetcher principal de arriba: atajo por
// DICTAMEN del vigía (la petición está condenada y sin timeout propio el usuario
// se comería el del sistema) y caída por FORMA-DE-RED.
//
// Si la bodega tampoco puede (documento sin bajar, dueño ajeno, web) devuelve
// `null` y se conserva EXACTAMENTE el comportamiento de hoy: el resumen no se
// pinta. Cero copy nuevo — i18n está fuera del alcance de esta tanda.
/* TANDA 32b (#294) — Y AHORA SABE DE TEMAS.
   `temas` es la lista PLANA de nombres palomeados, la misma forma que ya viaja
   al arranque y la misma con la que el runtime local recorta el pool. Los tres
   caminos de esta función la llevan —dictamen offline, servidor y caída por
   forma-de-red—, porque un resumen filtrado en un camino y entero en otro sería
   exactamente el número que cambia según haya señal.
   `topics` viaja SÓLO si hay algo palomeado, con `spread` condicional y no con
   un campo por default: sin selección el cuerpo que se postea es byte-idéntico
   al de antes de esta tanda. Es la misma regla del arranque (TANDA 29). */
export async function fetchFlashcardSummary(userId, documentIds, temas) {
  const conTemas = temas && temas.length > 0 ? temas : undefined;
  if (IS_APP_BUILD && isExplicitlyOffline()) {
    try {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.flashcardSummaryFromWarehouse(userId, documentIds, conTemas);
      if (fromWarehouse) return fromWarehouse;
    } catch (e) { /* sin bodega utilizable → camino normal de abajo */ }
  }
  try {
    const res = await api.post('/study/flashcards/progress-summary', {
      document_ids: documentIds,
      ...(conTemas ? { topics: conTemas } : {}),
    });
    return res.data;
  } catch (err) {
    if (IS_APP_BUILD && isNetworkShapedError(err)) {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.flashcardSummaryFromWarehouse(userId, documentIds, conTemas);
      if (fromWarehouse) return fromWarehouse;
    }
    throw err;
  }
}

/**
 * TANDA 29 (#271) — LOS TEMAS DE LOS DOCUMENTOS ELEGIDOS, PARA EL SELECTOR.
 *
 * MISMO PATRÓN, LÍNEA POR LÍNEA, que `fetchStudyCenterData` de aquí arriba: en
 * la app, con DICTAMEN explícito del vigía, la bodega contesta de inmediato (sin
 * eso, en modo avión el usuario se comería el timeout del sistema mirando el
 * spinner, porque axios no fija timeout propio); con red se pide al servidor
 * como siempre, y sólo un fallo de FORMA-DE-RED cae a la bodega.
 *
 * EN LA WEB ESTO ES UNA LLAMADA Y NADA MÁS: `IS_APP_BUILD` es falso, así que
 * ninguna de las dos puertas de bodega existe siquiera.
 *
 * Se pide AL ABRIR el selector, jamás al montar la pantalla: un renglón que
 * dispara una petición sólo por existir le cobraría al 100% de los usuarios el
 * costo de una función que abre una minoría.
 */
export async function fetchStudyTopics(userId, documentIds) {
  if (IS_APP_BUILD && isExplicitlyOffline()) {
    try {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.topicsFromWarehouse(userId, documentIds);
      if (fromWarehouse) return fromWarehouse;
    } catch (e) { /* sin bodega utilizable → camino normal de abajo */ }
  }
  try {
    const res = await api.post('/study/topics', { document_ids: documentIds });
    return res.data;
  } catch (err) {
    if (IS_APP_BUILD && isNetworkShapedError(err)) {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.topicsFromWarehouse(userId, documentIds);
      if (fromWarehouse) return fromWarehouse;
    }
    throw err;
  }
}

/* ── t7-1b (T5) -- LA CARD DEL GRUPO COMO ENTRADA DE LA LISTA ──────────────────
   Una card es lo que el profe publicó ("#k" de un documento de su banco): el
   documento, sus temas y la foto de las preguntas. Para esta pantalla es una
   entrada más de la lista, con la forma de un documento (`filename`,
   `question_count`, `flashcard_count`) para que el renglón, los conteos y las
   perillas la lean sin saber que existe, más lo suyo: `delGrupo`,
   `publicacion_id` y `doc_id` (el documento del profe).

   SU ID NO PUEDE CHOCAR CON EL DE UN DOCUMENTO, y por eso lleva prefijo: dos
   cards del mismo documento son dos entradas, y el id de un documento es un uuid
   que jamás empieza así. Sólo las ACTIVAS entran: una caducada no se estudia
   (Anexo A 2), y su aviso vive en el Panel. Sin flashcards: la card no las
   ofrece. */
const PREFIJO_CARD = 'pub:';
const esIdDeCard = (id) => typeof id === 'string' && id.startsWith(PREFIJO_CARD);
// El 403 de una card caducada (T3, `routes/study.py`). Cada pantalla pinta el suyo.
const MATERIAL_CADUCADO = 'group_material_not_available';

function entradasDeCards(grupos) {
  const entradas = [];
  for (const grupo of Array.isArray(grupos) ? grupos : []) {
    for (const card of (grupo && Array.isArray(grupo.cards)) ? grupo.cards : []) {
      if (!card || card.estado !== 'activa') continue;
      entradas.push({
        delGrupo: true,
        id: `${PREFIJO_CARD}${card.id}`,
        publicacion_id: card.id,
        doc_id: card.doc_id,
        nombre: card.nombre,
        filename: card.nombre,
        status: 'ready',
        question_count: card.total || 0,
        flashcard_count: 0,
      });
    }
  }
  return entradas;
}

/* Los temas de una selección CON cards, para "Elige temas". El servidor contesta
   con la MISMA forma que para un documento propio (T3), así que la ventana la pinta
   igual. Sin camino de bodega: la card no existe en la app (Anexo A 2).
   t7-1c (#478): una sola llamada para toda la selección. `documentIds` ya trae los
   propios elegidos y el documento de CADA card; con UNA card viaja
   `publicacion_id` (con una card sola, el cuerpo de t7-1b exacto) y con dos o más,
   `publicacion_ids`, nunca las dos. El servidor contesta una entrada por card
   (con su `publicacion_id`) y después los documentos propios. */
/* t7-3 (A1 y A4, #557 y #558): EN LA APP las cards tambien se listan. Con red, la MISMA
   llamada que la web; si no hay red o falla, la ultima lista que llego con red (la foto de la
   bodega), con la caducidad medida contra el reloj del telefono. En la web, la llamada de
   siempre y nada mas. */
async function cargarGruposParaEstudiar(user) {
  if (!IS_APP_BUILD) return cargarMaterialDelGrupo(user);
  const vivos = isExplicitlyOffline() ? null : await cargarMaterialDelGrupo(user, null);
  if (vivos !== null) return vivos;
  try {
    const m = await import('../lib/warehouse/cardsDelGrupo');
    const foto = await m.leerGruposDeBodega();
    return Array.isArray(foto) ? m.gruposSinRed(foto) : [];
  } catch (e) {
    return [];
  }
}

async function fetchTemasConCards(documentIds, cards) {
  const res = await api.post('/study/topics', {
    document_ids: documentIds,
    ...(cards.length === 1
      ? { publicacion_id: cards[0].publicacion_id }
      : { publicacion_ids: cards.map((c) => c.publicacion_id) }),
  });
  return res.data;
}

/* t7-3 (A4): los temas de una seleccion en la app. Con una card elegida se leen de la bodega
   (su paquete ya esta en el telefono: sin el, la puerta de estudio ni deja abrir el selector);
   sin cards, el camino de siempre. */
async function temasDeLaSeleccion(userId, documentIds, conCards) {
  if (IS_APP_BUILD && conCards) {
    const sources = await import('../lib/warehouse/screenSources');
    const fromWarehouse = await sources.topicsFromWarehouse(userId, documentIds);
    if (fromWarehouse) return fromWarehouse;
  }
  return fetchStudyTopics(userId, documentIds);
}

// pantallas-alumno (C): ver `enviados` dentro de la pantalla.
function sigueEnLaFila(doc, enviados) {
  if (!doc || !Object.prototype.hasOwnProperty.call(enviados, doc.id)) return false;
  return doc.status === 'partial' && (doc.retry_count ?? 0) === enviados[doc.id];
}

// pantallas-alumno (C, firma 1): lo que el reintento del documento parcial acepta. Un documento sin
// preguntas que NO esta en `partial` (el carril de troceo por temas puede cerrar `ready` con cero
// preguntas, services/ai.py:7446-7450) no entra: el reintento no lo tomaria.
function faltanPreguntas(doc) {
  return Boolean(doc) && !doc.delGrupo && doc.status === 'partial' && (doc.question_count || 0) === 0;
}

export default function StudyCenter() {
  // PAGER-1 — ¿el usuario está viendo ESTA raíz? Sin proveedor arriba: `true`,
  // que es la verdad de hoy (el router monta una sola raíz). En PAGER-2 el
  // proveedor lo pone el pager por encima. Ver lib/pager/activacion.js.
  const activa = useRaizActiva();
  // smoke-alumno, S1: al entrar al Centro la vista empieza ARRIBA. El chasis (el `<main>` de la web, o el scroller
  // de la hoja en la app) conserva el desplazamiento de la pantalla anterior, y al volver de unos resultados el
  // Centro aparecia a media pagina. Una escritura al despertar la raiz (la doctrina de `activacion.js`: dormida no
  // toca el chasis), la misma que hacen Analiticas y Ajustes.
  const scrollportDeLaRaiz = useScrollportRaiz();
  useLayoutEffect(() => {
    if (!activa) return;
    const scrollport = scrollportDeLaRaiz();
    if (scrollport) scrollport.scrollTop = 0;
  }, [activa, scrollportDeLaRaiz]);
  // pantallas-alumno (C): `dashboard` tambien (P1 y los avisos del reintento usan textos del Panel).
  const { t, ready } = useReadyTranslation(['study', 'common', 'dashboard']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // El fetcher lleva el user.id para la guardia de dueño del fallback de
  // bodega (F6.4b). Identidad nueva por render sin costo: useScreenData
  // guarda el fetcher en un ref (cambiarlo no re-dispara fetches).
  // PAGER-1: RED (`GET /documents` + `GET /study/history`, este último en el
  // carril `study_user` 150/h). `enabled` es la puerta que `useScreenData` ya
  // tenía; dormida no fetchea y al activarse fetchea una vez.
  const { data, loading, error, setData, refetch } = useScreenData('study-center', () => fetchStudyCenterData(user?.id), { enabled: activa });
  /* pantallas-alumno (C, firma 1 de la parte 1): los documentos que "Generate Missing Questions"
     ya mando a la fila salen de la lista SIN esperar a recargar. Se guarda su `retry_count` al
     mandarlo: mientras el servidor lo siga contando como el mismo `partial` (el reintento aun no
     corre), no vuelve a pintarse como estudiable; cuando el reintento corre pasa a `processing` (y
     la lista ya no lo trae), y si vuelve a `partial` trae otro intento y reaparece. Web y app por el
     mismo camino: la copia local de la app pasa por aqui tambien. */
  const [enviados, setEnviados] = useState({});
  // useMemo: identidad estable para el effect de preselección (exhaustive-deps).
  const docs = useMemo(
    () => (data?.docs || []).filter((d) => !sigueEnLaFila(d, enviados)),
    [data, enviados]);
  const bloqueados = useMemo(() => data?.bloqueados || [], [data]);
  const completedSessions = data?.completedSessions || 0;
  // F6.4b: el dato vino de la bodega (fetch vivo caído por red).
  // F6.7b (fase 2): más el DICTAMEN del vigía, que además apaga y prende esto
  // en vivo — el mensaje honesto del CTA deja de quedarse pegado hasta navegar.
  const net = useNetworkStatus();
  const isOffline = Boolean(data?.offline) || net.isOffline;
  // F6.7b (fase 1) — el inventario del teléfono: la disponibilidad POR MODO y
  // el mensaje multi-doc salen de aquí, no de los conteos del servidor.
  const { presenceById } = useLocalPresence();
  // El candado D-G: null = nadie lo ha disparado; 'free'|'guest' = su variante.
  const [proGateVariant, setProGateVariant] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  /* COS-27 — EL MODO TAMBIÉN PUEDE VENIR EN LA URL.
     Hermano del `?doc=` que esta pantalla ya lee más abajo: quien invita a
     estudiar desde otra pantalla puede además decir CON QUÉ MODO (hoy, las dos
     frases del estado vacío de Analíticas). La lista blanca son los ids que
     esta misma pantalla pinta (`MODES_VISUAL`), así que un valor desconocido
     —o ninguno— cae en el `adaptive` de siempre: cero mecanismos nuevos y cero
     cambio para todo el que llega sin el parámetro.
     Va en el valor INICIAL, no en un efecto: el modo no depende de que la lista
     de documentos llegue (el `?doc=` sí, por eso aquél espera), y así la
     pantalla nunca pinta un modo para cambiarlo al frame siguiente.

     PAGER-1 — LA URL ES DEL CHASIS, NO DE LA PESTAÑA. Montadas a la vez, las
     cuatro raíces leerían el MISMO query string, y un `?mode=` dirigido a esta
     pantalla no puede preseleccionar nada en una raíz que el usuario no está
     viendo. El gate va DENTRO del inicializador y no en un efecto a propósito:
     mover la lectura a un efecto pintaría 'adaptive' y lo cambiaría al frame
     siguiente, que es justo lo que la decisión de arriba prohíbe. Consecuencia
     asumida para PAGER-2: una raíz que nace dormida no recupera el `?mode=` al
     activarse — y no le hace falta, porque un enlace con `?mode=` aterriza en
     ESTA ruta, o sea que nace activa. */
  const [mode, setMode] = useState(() => {
    if (!activa) return 'adaptive';
    const pedido = searchParams.get('mode');
    return MODES_VISUAL.some(m => m.id === pedido) ? pedido : 'adaptive';
  });

  /* pantallas-alumno (C, P9): AL VOLVER DE RESULTADOS, EL MODO DEL QUE SE VIENE.
     Los resultados del examen, los de Adaptive y el resumen de Flashcards regresan aqui con
     `/study?mode=exam|adaptive|flashcards`. En la web eso es un montaje nuevo y lo lee el valor
     inicial de arriba. En la app el Centro vive montado en el carril y NO se vuelve a montar: el
     valor inicial ya corrio (dormido, o con otra URL), asi que se aplica aqui, UNA vez por visita,
     al activarse con un `?mode=` valido. La marca se borra al dormirse: la siguiente vuelta con
     `?mode=` se vuelve a aplicar. Entrar por el menu (sin `?mode=`) no toca nada: lo de hoy. */
  const modoDeLaUrlRef = useRef(activa ? searchParams.get('mode') : null);
  useEffect(() => {
    if (!activa) { modoDeLaUrlRef.current = null; return; }
    const pedido = searchParams.get('mode');
    if (!pedido || pedido === modoDeLaUrlRef.current) return;
    modoDeLaUrlRef.current = pedido;
    if (MODES_VISUAL.some((m) => m.id === pedido)) setMode(pedido);
  }, [activa, searchParams]);

  /* ── t7-1b (T5) -- EL MATERIAL DEL GRUPO ───────────────────────────────────
     Se pide UNA vez al montar, con la raíz activa (la misma puerta que el
     `?doc=`), sólo en la web y sólo con cuenta: en la app y para un invitado ni
     se llama (`lib/materialDelGrupo` lo vuelve a comprobar). `null` es "todavía
     no llega"; la petición nunca rechaza (un fallo se ve como `[]`).

     LAS CARDS VAN ARRIBA de los documentos propios, en el orden del servidor: es
     lo que el alumno vino a estudiar cuando llega desde la tarjeta del Panel con
     `?pub=`, y la lista tiene tope de alto. Sin cards, `todas` ES el mismo
     arreglo `docs`: la lista, la poda y los conteos de un alumno sin grupos son
     los de siempre. */
  // t7-3 (A1, #557): la compuerta `IS_APP_BUILD` se retiro: la app tambien lista las cards.
  const pideMaterial = activa && Boolean(user?.id) && !user?.is_guest;
  const [materialDelGrupo, setMaterialDelGrupo] = useState(null);
  useEffect(() => {
    if (!pideMaterial) return undefined;
    let cancelado = false;
    cargarGruposParaEstudiar(user).then((grupos) => { if (!cancelado) setMaterialDelGrupo(grupos); });
    return () => { cancelado = true; };
    // `user` entra por su id: un objeto nuevo del mismo usuario no vuelve a pedir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pideMaterial, user?.id]);
  const materialPendiente = pideMaterial && materialDelGrupo === null;
  const entradasDelGrupo = useMemo(() => entradasDeCards(materialDelGrupo), [materialDelGrupo]);
  const todas = useMemo(
    () => (entradasDelGrupo.length ? [...entradasDelGrupo, ...docs] : docs),
    [entradasDelGrupo, docs]);
  // t7-1c (#478): una card entra y sale de la selección como un documento (ver
  // `toggleDoc`), sola, con otras cards o con documentos propios.
  // `seleccionEsCard` dice si hay ALGUNA card elegida (con una, flashcards no se
  // ofrece); `cardsElegidas` son sus entradas en el ORDEN en que se eligieron (el
  // de `selectedDocs`). Sin cards es la lista vacía y nada de abajo cambia.
  const seleccionEsCard = selectedDocs.some(esIdDeCard);
  const cardsElegidas = seleccionEsCard
    ? selectedDocs.filter(esIdDeCard)
      .map((id) => entradasDelGrupo.find((e) => e.id === id))
      .filter(Boolean)
    : [];
  // Los documentos que viajan con cards: los propios elegidos (en el orden en que
  // se eligieron) y después el de CADA card (en el mismo orden, una aparición por
  // card aunque dos cards sean del mismo documento). Sólo se usa con cards.
  const documentosConCards = () => [
    ...selectedDocs.filter((id) => !esIdDeCard(id)),
    ...cardsElegidas.map((c) => c.doc_id),
  ];
  // F6.5b — LA PUERTA OFFLINE: el gate del CTA ya no es "offline == cerrado"
  // sino la función ÚNICA de disponibilidad por (modo, docs) del cerebro
  // (lib/brain/studyAvailability): Adaptive post-corte corre 100% local y
  // abre sin red cuando el pool está en bodega; Exam/Flashcards siguen
  // gateados por conexión (F6.6 es su dueño); F6.7 enchufa D-G ahí mismo
  // sin volver a tocar esta pantalla. En web la función devuelve siempre
  // disponible-salvo-offline (que en web nunca ocurre): render byte-idéntico.
  const [availability, setAvailability] = useState({ available: true });
  const selectedKey = selectedDocs.join('|');
  // F6.7a — el contador que vuelve a hacer la pregunta cuando el material
  // aterriza. El diagnóstico del 22-jul dejó claro por qué hacía falta: cuando
  // la fila del doc llega a la bodega, NADA re-evaluaba la disponibilidad (los
  // deps de este effect no cambian con una descarga), así que el mensaje "aún
  // no está en tu dispositivo" se quedaba pegado hasta cambiar de modo o
  // re-entrar. Sube con el MISMO evento post-commit que refresca las listas: el
  // copy no cambia, solo deja de mentir.
  const [materialTick, setMaterialTick] = useState(0);
  useEffect(() => {
    const onMaterialReady = () => setMaterialTick((n) => n + 1);
    window.addEventListener('studybuddy:local-material-ready', onMaterialReady);
    return () => window.removeEventListener('studybuddy:local-material-ready', onMaterialReady);
  }, []);
  useEffect(() => {
    let cancelled = false;
    studyAvailability({ mode, documentIds: selectedDocs, offline: isOffline })
      .then((a) => { if (!cancelled) setAvailability(a); })
      .catch(() => { if (!cancelled) setAvailability({ available: true }); });
    return () => { cancelled = true; };
    // selectedKey representa a selectedDocs (identidad estable por contenido)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedKey, isOffline, materialTick]);

  /* ── B3 (13-sep) — LA PUERTA VA A BUSCAR LO QUE DICE QUE FALTA ────────────
     EL CASO, leído en la base real (13-sep, cuenta Free, app en iPhone; la
     persona no se nombra: se la identifica por su documento). Sube un PDF desde
     la app a las 16:26:35, se sale a otras apps, el servidor termina a las
     16:28:05 con la app EN SEGUNDO PLANO, vuelve a las 16:28:40 y el Simulador
     no deja empezar —«Este material aún no está en tu dispositivo»—. Entre
     16:29:34 y 16:29:56 entra y sale del Centro de Estudio SEIS veces, y a las
     16:30:03 por fin arranca. Dos minutos y seis intentos. Con el segundo
     documento del día, que terminó de procesarse con la pantalla a la vista,
     fueron 24 segundos y cero idas y vueltas.

     EL AGUJERO: `pool_not_ready` promete en su copy que el material «se descarga
     solo cuando hay conexión», y esta pantalla no arrancaba ninguna descarga al
     decirlo. La única convergencia que dispara cuelga de la ACTIVACIÓN de la
     raíz (el efecto de `study-center-mount`, más abajo), así que si ESA
     oportunidad no trajo el material —y volver del segundo plano es justo cuando
     más fácil es que no lo traiga: iOS descongela el WebView y la primera
     lectura de red suele fallar— al alumno sólo le quedaba salir y volver, que
     es exactamente lo que la telemetría muestra que pasó seis veces.

     LA REGLA: cuando el veredicto es «no está en este aparato», se va a buscar
     EN ESE MOMENTO. No es un reloj ni una vigilancia —lo que este arco tiene
     prohibido—: es una reacción a un hecho que acaba de ocurrir, como el
     montaje, la señal que vuelve o el segundo plano que se acaba.

     Y NO PUEDE VOLVERSE UN BUCLE, que es el riesgo de toda petición que nace de
     un veredicto: se pide UNA vez POR SELECCIÓN, y la marca sólo se borra cuando
     la puerta ABRE. Así, la re-evaluación que dispara cada aterrizaje de
     material —la de `materialTick`, aquí arriba— no puede encadenar una segunda
     petición, ni siquiera en el frame en que el veredicto viejo todavía está en
     el estado y el nuevo aún no llegó. Si la descarga falla, quien insiste es el
     cartero con su escalera (`warehouse/downloader.js`, 5 s → 15 s → 45 s) y los
     disparadores que ya existían (montar la pantalla, volver del segundo plano,
     recuperar la señal); esta pantalla pregunta una vez y se calla.

     `activa`: una pane dormida no pide nada (doctrina PAGER-1). Sin selección
     tampoco: la puerta contesta «disponible» con la lista vacía, así que este
     caso no se alcanza, y el guard lo deja escrito. En web, inerte por
     `IS_APP_BUILD` — ni siquiera se pide el chunk. */
  const materialPedidoRef = useRef(null);
  useEffect(() => {
    if (!IS_APP_BUILD || !activa) return;
    if (availability.available) { materialPedidoRef.current = null; return; }
    if (selectedDocs.length === 0) return;
    if (availability.reason !== STUDY_UNAVAILABLE_REASON.POOL_NOT_READY) return;
    if (materialPedidoRef.current === selectedKey) return;
    materialPedidoRef.current = selectedKey;
    import('../lib/warehouse/converge')
      .then((c) => c.convergeWarehouse(c.CONVERGE_REASON.STUDY_GATE))
      .catch(() => {});
    // selectedKey representa a selectedDocs (identidad estable por contenido)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, selectedKey, activa]);

  /* ── TANDA 29 (#272/#273) — EL ESTADO DEL SELECTOR DE TEMAS ────────────────
     TRES cosas y ninguna se persiste: ni `localStorage`, ni servidor, ni
     bodega. Es una decisión de UNA VISITA, igual que el número de preguntas del
     examen — salir de la pantalla la olvida, y eso es lo correcto: un filtro
     pegado que el usuario puso hace tres días y ya no recuerda es una sesión
     misteriosamente corta sin explicación a la vista.

     POR MODO, y por eso es un objeto y no un arreglo: el examen y las
     flashcards no comparten ni los temas (un tema puede tener tarjetas y no
     preguntas) ni la intención. Cambiar de modo conserva lo de cada uno.

     `temasPorModo` guarda lo palomeado; `catalogoDeTemas` guarda lo que el
     endpoint devolvió para la selección de documentos vigente (para poder
     pintar "2 de 7" sin volver a preguntar al reabrir). */
  const [temasPorModo, setTemasPorModo] = useState({ adaptive: [], exam: [], flashcards: [] });
  const [selectorAbierto, setSelectorAbierto] = useState(false);
  const [catalogoDeTemas, setCatalogoDeTemas] = useState(null);
  const [temasCargando, setTemasCargando] = useState(false);
  /* #413 — ¿LA ÚLTIMA PETICIÓN DE TEMAS SE MURIÓ? Es la única manera de que la
     ventana distinga sus dos vacíos: «no se pudo cargar» y «este documento no
     tiene temas para este modo». Se pone en `true` SÓLO en el `catch` de
     `fetchStudyTopics` y se limpia al volver a pedir o al cambiar de selección:
     un fallo viejo no puede teñir una ventana nueva. */
  const [cargaFallida, setCargaFallida] = useState(false);

  /* ── TANDA 29 (#273) — CAMBIAR DE DOCUMENTOS REINICIA LA SELECCIÓN ────────
     Quitar o añadir un documento cambia el universo de temas: los que el
     usuario había palomeado pueden no existir en el conjunto nuevo, y arrancar
     con un filtro que ya no aplica sirve una sesión rara o vacía sin que nada en
     pantalla lo explique. Se reinicia a "todos", que es el default honesto.

     Se reinician LOS TRES MODOS, no sólo el activo: el catálogo que quedaría
     cacheado es el de la selección vieja para todos ellos.

     LOS DOS `set` SE BAILAN SOLOS EN EL CASO NORMAL: React descarta un `set` que
     devuelve el mismo valor, así que un montaje —o cualquier cambio de
     documentos sin nada palomeado, que es el 100% del uso de hoy— no provoca ni
     un re-render de más. */
  useEffect(() => {
    setTemasPorModo((previo) => (
      previo.adaptive.length || previo.exam.length || previo.flashcards.length
        ? { adaptive: [], exam: [], flashcards: [] }
        : previo));
    setCatalogoDeTemas(null);
    // #413: el fallo es de UNA petición, no del estado de la pantalla. Cambiar
    // de documentos estrena petición, así que estrena también su veredicto.
    setCargaFallida(false);
    // #414: y la ventana no puede quedarse abierta hablando de una selección
    // que ya no existe. Su catálogo se acaba de tirar justo aquí arriba: sin
    // esto quedaría abierta, vacía y diciendo «este documento no tiene temas».
    setSelectorAbierto(false);
  }, [selectedKey]);

  /* ── #414 — UN DOCUMENTO QUE YA NO ESTÁ EN LA LISTA NO PUEDE SEGUIR ELEGIDO ──
     EL CASO (bug 3 del smoke): se elige un documento en Estudiar, se va al
     Panel, se borra, y se vuelve. En la app las cuatro raíces viven montadas a
     la vez, así que esta pantalla conserva su selección — apuntando a un id que
     ya no existe. Y nada la corregía: `availability` no se re-evalúa (sus deps
     no cambian con un borrado, y `barrerDocBorrado` no emite
     `local-material-ready`), así que el botón de temas se quedaba ENCENDIDO, la
     ventana abría y el servidor contestaba `{"documents": []}` con 200.

     UN SOLO PUNTO DE PODA, y colgado de lo que la pantalla ya tiene: `docs`
     cambia por `docs-changed` (con payload o por `refetch`), por el fetch de
     activación y por el payload del sondeo. No hace falta escuchar el borrado.

     `return prev` CUANDO NADA CAMBIA, y no es cosmética: `selectedDocs` alimenta
     `selectedKey`, que es la dep de la evaluación de disponibilidad. Un arreglo
     nuevo por cada refresco de lista dispararía una evaluación por cada uno.

     LA GUARDA DE `loading`/`error` ES LOAD-BEARING: sin ella, un refetch que
     falla dejaría `docs` vacío un instante y borraría una selección legítima.

     Web y app por el MISMO camino: aquí no hay `IS_APP_BUILD`.

     t7-1b (T5): lo vivo es `todas` (las cards del grupo más los documentos), así
     que una card elegida no se poda cuando la lista de documentos se refresca.
     Sin cards, `todas` es `docs` y esto es lo de siempre. */
  useEffect(() => {
    if (!activa) return;
    if (loading || error) return;
    setSelectedDocs((prev) => {
      if (prev.length === 0) return prev;
      const vivos = new Set(todas.map((d) => d.id));
      const podado = prev.filter((id) => vivos.has(id));
      return podado.length === prev.length ? prev : podado;
    });
  }, [todas, activa, loading, error]);

  /* Lo palomeado en el modo que se está viendo. Nunca `undefined`.

     ── HOTFIX (#342) — VA EN SU PROPIO `useMemo`, Y NO ES COSMÉTICA LA RAZÓN ──
     El `|| []` es una expresión lógica: el día que el lado izquierdo faltara,
     cada render fabricaría un arreglo NUEVO. Y esta lista viaja tal cual en la
     lista de dependencias del `useMemo` de `topesPorTema`, más abajo — una
     identidad distinta por render volvería ese memo inútil. `exhaustive-deps` lo
     ve y lo avisa; en el build de la app, con `CI=true`, un aviso de esa regla es
     un build roto. Así murió el de la tanda 32, que fue quien estrenó el memo.

     EL VALOR NO CAMBIA. Con los tres modos siempre presentes en `temasPorModo`
     el `|| []` no dispara hoy, y el día que dispare devolverá el mismo arreglo
     vacío en vez de uno nuevo cada vez. Lo único que cambia es la IDENTIDAD, que
     es justo lo que el memo de abajo necesita que sea estable. */
  const temasElegidos = useMemo(() => temasPorModo[mode] || [], [temasPorModo, mode]);
  /* TANDA 32b (#294) — la identidad POR CONTENIDO de esa lista, para las deps de
     los efectos. Hermana de `selectedKey`, que hace lo mismo con los documentos
     desde la 29.

     EL SEPARADOR NO PUEDE SER UNO CUALQUIERA. `selectedKey` usa `|` y le basta
     porque un id de documento es un uuid; aquí los elementos son NOMBRES DE TEMA
     escritos por el modelo, y un nombre puede llevar cualquier carácter visible
     —espacios y barras incluidos: "Cardiovascular System", "A/B testing"—. Con un
     separador que quepa dentro de un nombre, dos selecciones distintas darían la
     misma clave y el resumen no se volvería a pedir.
     Va como ESCAPE y no como carácter literal: un NUL escrito de verdad vuelve el
     archivo binario para grep y no se ve en un diff. */
  const temasKey = temasElegidos.join('\u0000');

  /* El "de N" del renglón: cuántos temas DISTINTOS ofrece el modo actual. Se
     cuenta por NOMBRE y no por tarjeta porque el filtro viaja por nombre — un
     mismo tema presente en dos documentos es UNA casilla lógica, aunque la lista
     lo muestre bajo sus dos encabezados. (Repetidos exactos entre partes de un
     libro: cero en el corpus medido — censo de temas §H.4.)
     Sin catálogo todavía, el total cae al tamaño de la selección: es lo único
     honesto que se puede decir, y sólo se ve en el instante entre palomear y
     que el catálogo esté en mano. */
  const totalDeTemas = useMemo(() => {
    if (!catalogoDeTemas) return temasElegidos.length;
    const campo = mode === 'flashcards' ? 'flashcard_count' : 'question_count';
    const nombres = new Set();
    for (const doc of catalogoDeTemas.documents || []) {
      for (const tema of doc.topics || []) {
        if ((tema[campo] || 0) > 0) nombres.add(tema.name);
      }
    }
    return nombres.size;
  }, [catalogoDeTemas, mode, temasElegidos.length]);

  /* #407 — «ELIGE TEMAS» APAGADO MIENTRAS EL MATERIAL NO ESTÁ EN EL APARATO.
     Hasta aquí el botón sólo se apagaba sin documento elegido. Faltaba el otro
     estado en que abrirlo no sirve de nada: CON documento elegido pero con el
     material todavía fuera del aparato. Ahí la puerta contesta `pool_not_ready`
     y el catálogo de temas sale de la bodega local —que aún está vacía—, así que
     el selector abría SIN UNA SOLA FILA y sin explicar por qué (el propio
     TopicPickerModal lo tiene escrito: «CERO COPY PROPIO PARA EL FALLO»).
     No hay camino nuevo: se reusa tal cual la mecánica del caso sin documento
     —abuelo con la opacidad y el banner, padre con los eventos apagados, botón
     deshabilitado— y lo único que cambia es la CONDICIÓN. En web esto es inerte:
     la puerta nunca devuelve `pool_not_ready` ahí. */
  const materialFuera = availability.reason === STUDY_UNAVAILABLE_REASON.POOL_NOT_READY;
  const temasApagados = selectedDocs.length === 0 || materialFuera;

  const abrirSelectorDeTemas = async () => {
    if (temasApagados) return;
    setSelectorAbierto(true);
    // El catálogo se pide UNA vez por selección de documentos: reabrir la
    // ventana no vuelve a pegarle al servidor.
    if (catalogoDeTemas) return;
    setTemasCargando(true);
    setCargaFallida(false);          // #413: se vuelve a pedir, el fallo viejo se va
    try {
      // t7-1b (T5): una card pide SUS temas (con `publicacion_id`); un documento
      // propio, el cuerpo de siempre.
      // t7-1c (#478): con cards, UNA llamada con toda la selección (propios y
      // cards); sin cards, el cuerpo de siempre.
      // t7-3 (A4): en la app una card se estudia en el telefono con su id local (`pub:<id>`),
      // asi que sus temas salen de su paquete en la bodega, como los de un documento propio.
      const datos = cardsElegidas.length > 0 && !IS_APP_BUILD
        ? await fetchTemasConCards(documentosConCards(), cardsElegidas)
        : await temasDeLaSeleccion(user?.id, selectedDocs, cardsElegidas.length > 0);
      setCatalogoDeTemas(datos);
    } catch (e) {
      // t7-1b (T5): la card caducó entre el Panel y aquí. Su aviso (texto 13) y de
      // vuelta al Panel, que es donde ya se ve bloqueada. No es un fallo de carga.
      if (e?.response?.data?.error_code === MATERIAL_CADUCADO) {
        setSelectorAbierto(false);
        toast.error(t('common:errors.group_material_not_available'));
        navigate('/dashboard');
        return;
      }
      // El aviso de la casa, sin copy nuevo. El renglón sigue en "Todos los
      // temas": nada se filtró.
      // #413: y la ventana deja de quedarse muda — este es el único sitio del
      // repo que enciende `cargaFallida`.
      // pantallas-alumno (C, P4): sin aviso flotante. Basta el texto rojo de la ventana (CE4-e).
      setCargaFallida(true);
    } finally {
      setTemasCargando(false);
    }
  };

  const alternarTema = (nombre) => {
    setTemasPorModo((previo) => {
      const actual = previo[mode] || [];
      const siguiente = actual.includes(nombre)
        ? actual.filter((n) => n !== nombre)
        : [...actual, nombre];
      return { ...previo, [mode]: siguiente };
    });
  };

  const limpiarTemas = () => setTemasPorModo((previo) => ({ ...previo, [mode]: [] }));

  // F6.7a — convergencia al MONTAR el Centro de Estudio: la pantalla donde el
  // hueco se siente (es aquí donde el CTA se cierra con "aún no está en tu
  // dispositivo"). Preguntar al abrir es lo que convierte ese mensaje en algo
  // transitorio en vez de permanente.
  // PAGER-1: `study-center-mount` está en PHOTO_WAKE porque es un ACTO DEL
  // USUARIO y no un reloj (converge.js). Colgado de la activación, el motivo
  // vuelve a ser verdad.
  useEffect(() => {
    if (!activa) return;
    if (!IS_APP_BUILD || !user?.id) return;
    import('../lib/warehouse/converge')
      .then((c) => c.convergeWarehouse(c.CONVERGE_REASON.STUDY_CENTER_MOUNT))
      .catch(() => {});
  }, [user?.id, activa]);
  // Copy honesto POR RAZÓN (y por modo para la falta de conexión). La razón
  // reservada offline_requires_pro (D-G/F6.7) cae al mensaje genérico hasta
  // que su sprint traiga su propio copy — hoy ningún camino la retorna.
  const availabilityMessage = () => {
    if (availability.reason === STUDY_UNAVAILABLE_REASON.POOL_NOT_READY) {
      return t('common:offline.pool_not_ready');
    }
    if (availability.reason === STUDY_UNAVAILABLE_REASON.NO_CONNECTION_FOR_MODE) {
      if (mode === 'exam') return t('common:offline.exam_needs_connection');
      if (mode === 'flashcards') return t('common:offline.flashcards_needs_connection');
    }
    return t('common:offline.need_connection');
  };
  // post-t7-1 M4: lo que el aviso de la barra mide de mas sobre un renglon (0 con uno: la pagina queda igual)
  const [refDelAviso, extraDelAviso] = useExtraDelAviso();
  const [numQuestions, setNumQuestions] = useState([10]);
  const [timeLimit, setTimeLimit] = useState([30]);
  const [numQInput, setNumQInput] = useState('10');
  const [timeLimitInput, setTimeLimitInput] = useState('30');
  const [validationErrors, setValidationErrors] = useState({});
  const TIME_STEPS = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 90, 120, 180, 240, 300, 360];
  const nearestTimeStepIndex = (val) => {
    let best = 0;
    for (let i = 1; i < TIME_STEPS.length; i++) {
      if (Math.abs(TIME_STEPS[i] - val) < Math.abs(TIME_STEPS[best] - val)) best = i;
    }
    return best;
  };
  const handleNumQSlider = (v) => {
    setNumQuestions(v);
    setNumQInput(String(v[0]));
    setValidationErrors(prev => ({ ...prev, numQuestions: '' }));
  };
  const handleTimeLimitSlider = (v) => {
    const stepIndex = v[0];
    const actual = TIME_STEPS[stepIndex] || TIME_STEPS[0];
    setTimeLimit([actual]);
    setTimeLimitInput(String(actual));
    setValidationErrors(prev => ({ ...prev, timeLimit: '' }));
  };
  const handleNumQInputChange = (e) => {
    const raw = e.target.value;
    setNumQInput(raw);
    setValidationErrors(prev => ({ ...prev, numQuestions: '' }));
    const val = parseInt(raw);
    if (!isNaN(val) && val >= 1) setNumQuestions([Math.min(val, Math.max(topeQ, 1))]);
  };
  /* TANDA 32 (#298) — EL CUADRO SE TOPA AL SALIR DEL CAMPO, no mientras se
     teclea. Tecleando, el `onChange` de arriba ya sujeta la BARRA (que nunca
     rebasa el techo) pero deja el texto tal cual: si corrigiera letra por letra,
     escribir "11" con techo 9 se volvería "9" en cuanto entrara el "1" y el
     campo quedaría intecleable — el mismo argumento por el que el efecto de
     COS-35 se niega a mirar `numQInput`.
     Al salir del campo ya no hay nada a medio escribir, así que ahí sí se baja
     el texto al techo. Un campo vacío o no numérico no se toca: ése es asunto de
     la validación del arranque, que dice qué falta con su propio mensaje. */
  const handleNumQInputBlur = () => {
    if (topeQ < 1) return;
    const actual = parseInt(numQInput, 10);
    if (isNaN(actual) || actual <= topeQ) return;
    setNumQuestions([topeQ]);
    setNumQInput(String(topeQ));
  };
  const handleTimeLimitInputChange = (e) => {
    const raw = e.target.value;
    setTimeLimitInput(raw);
    setValidationErrors(prev => ({ ...prev, timeLimit: '' }));
    const val = parseInt(raw);
    if (!isNaN(val) && val >= 1) setTimeLimit([Math.min(val, 360)]);
  };
  // T1-C1 — Los dos campos numéricos del examen quedan seleccionados ENTEROS al
  // ganar foco, para que teclear reemplace el valor en vez de anexarse a él.
  // La selección se DIFIERE un tick: en WKWebView iOS coloca su caret DESPUÉS
  // de despachar 'focus', así que un select() síncrono dentro del handler se
  // pisa solo y el campo termina con el cursor donde cayó el dedo. rAF corre
  // justo antes del siguiente pintado, ya con el caret puesto, y sin el parpadeo
  // que deja un setTimeout(0) — setTimeout queda de respaldo por si no hay rAF.
  // El guard de activeElement evita robarle la selección al campo siguiente si
  // el foco ya se movió durante ese tick.
  // Sólo se engancha a focus, no a click: un segundo toque DENTRO del campo ya
  // enfocado no vuelve a disparar focus, así que ahí el cursor cae donde el
  // usuario tocó — el comportamiento nativo, intacto.
  const selectAllOnFocus = (e) => {
    const el = e.target;
    const selectAll = () => {
      if (document.activeElement === el && typeof el.select === 'function') el.select();
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(selectAll);
    else setTimeout(selectAll, 0);
  };
  const [flashcardSummary, setFlashcardSummary] = useState(null);
  const [regenState, setRegenState] = useState({ active: false, current: 0, total: 0 });
  // F4.3c (solo mobile): el párrafo de Adaptive colapsado a una línea + ⓘ, y el
  // desglose por documento de Flashcards plegado tras un tap — misma información,
  // menos altura, para que todo quepa en una pantalla de 375px.
  const [adaptiveInfoOpen, setAdaptiveInfoOpen] = useState(false);
  const [fcBreakdownOpen, setFcBreakdownOpen] = useState(false);
  const isGuest = user?.is_guest;
  // ── FLASH-3 (pieza 1) — EL CANDADO DE REENTRADA DE "INICIAR" ────────────────
  //
  // EL HECHO del device (re-smoke flash-2): DOS `DL.START exam-mount` a 601 ms.
  // La entrada del examen corrió dos veces, y la única forma de que eso pase con
  // la R53-b puesta es una SEGUNDA NAVEGACIÓN: `startSession` acuña un
  // `startToken` nuevo en cada tap, y ese token está en las dependencias de
  // `enterExam` — token nuevo, identidad nueva, el guardia de idempotencia no
  // muerde y la pantalla vuelve a cargar encima del examen ya pintado (censo
  // flash-2, F2-M2b).
  //
  // POR QUÉ DOS PIEZAS Y NO UNA. El REF es el candado de verdad: `startSession`
  // es `async` y tiene un `await` a media función, así que entre el primer tap y
  // el `navigate` hay una ventana real en la que un segundo tap —o un click
  // fantasma de WKWebView— entra por la puerta. Un ref se lee y se escribe en el
  // mismo turno, sin esperar a que React re-renderice: es lo único que puede
  // cerrar esa ventana. El ESTADO es solo la cara visible del candado (el botón
  // deshabilitado), y por eso no manda: si mandara, el segundo tap del mismo
  // frame pasaría igual, porque el render que lo deshabilita todavía no ocurrió.
  const startingRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const studyCenterViewedRef = useRef(false);

  /* PAGER-1 — UNA POR VISITA, Y LA VISITA ES LA ACTIVACIÓN (misma firma que
     `dashboard_viewed`): el ref se resetea en el cleanup de la DESACTIVACIÓN,
     así que una raíz que ya no se desmonta sigue contando visitas en vez de
     emitir una sola vez en toda la vida de la app. Con `activa` fija en true
     esto emite al montar y el cleanup sólo corre al desmontar, igual que hoy. */
  useEffect(() => {
    if (!activa) return undefined;
    if (!studyCenterViewedRef.current) {
      studyCenterViewedRef.current = true;
      logAnalyticsEvent('study_center_viewed');
    }
    return () => { studyCenterViewedRef.current = false; };
  }, [activa]);

  // Pre-select document from URL param — once, apenas la lista lo contenga
  // (con cache fresco eso es el primer render; con fetch, al llegar el dato).
  // PAGER-1: la URL es del chasis — sólo la raíz activa la lee. El ref
  // `preselectAppliedRef` se conserva, así que al activarse aplica UNA vez.
  const preselectAppliedRef = useRef(false);
  useEffect(() => {
    if (!activa) return;
    if (preselectAppliedRef.current) return;
    const preSelectId = searchParams.get('doc');
    if (!preSelectId || docs.length === 0) return;
    preselectAppliedRef.current = true;
    // t7-1b (T5): si ya hay una card elegida (llegó con `?pub=`), la card manda.
    if (docs.some(d => d.id === preSelectId)) setSelectedDocs((prev) => (prev.some(esIdDeCard) ? prev : [preSelectId]));
  }, [docs, searchParams, activa]);

  // tutor-1 (C): `?topic=<nombre>` (el chip "Practicar este tema" del Tutor), con la disciplina de `?doc=`: raiz
  // activa, UNA vez por montaje, en cuanto llegan los temas del documento preseleccionado. Si un tema de ese documento
  // coincide con el nombre (sin distinguir mayusculas ni espacios de mas), queda elegido SOLO ese en "Choose topics" del
  // modo que se ve; si no coincide, nada cambia (todos, como hoy). En adaptativa no hay renglon de temas (#271), asi que
  // ahi no aplica.
  const preselectTopicRef = useRef(false);
  const seleccionDelTemaRef = useRef('');
  seleccionDelTemaRef.current = selectedDocs.join('|');
  const modoDelTemaRef = useRef(mode);
  modoDelTemaRef.current = mode;
  useEffect(() => {
    if (!activa || preselectTopicRef.current) return;
    const nombre = searchParams.get('topic');
    const docId = searchParams.get('doc');
    if (!nombre || !docId || selectedDocs.length !== 1 || selectedDocs[0] !== docId) return;
    preselectTopicRef.current = true;
    const plano = (x) => String(x || '').replace(/\s+/g, ' ').trim().toLowerCase();
    temasDeLaSeleccion(user?.id, [docId], false).then((datos) => {
      if (!datos || seleccionDelTemaRef.current !== docId) return;
      const doc = (datos.documents || []).find((d) => (d.document_id || d.id) === docId);
      const hallado = ((doc && doc.topics) || []).find((tema) => plano(tema.name) === plano(nombre));
      setCatalogoDeTemas(datos);
      const modo = modoDelTemaRef.current;
      if (hallado && modo !== 'adaptive') setTemasPorModo((previo) => ({ ...previo, [modo]: [hallado.name] }));
    }).catch(() => {});
  }, [activa, searchParams, selectedDocs, user?.id]);

  /* t7-1b (T5) -- `?pub=<id>`: la tarjeta de la card en el Panel manda aquí con
     `?doc=<documento del profe>&pub=<id>`. La misma disciplina que el `?doc=` de
     arriba: raíz activa, UNA vez por montaje, en cuanto las cards llegan; si la
     card no está (caducada, ajena), el ref se consume y no se reintenta. Con
     `pub` manda la card: el documento del profe no está en la lista del alumno,
     y aunque lo estuviera, la card lo reemplaza. En flashcards el modo cae a
     práctica, en el mismo lote: la card no las ofrece.
     Remate de la revision global: se espera a que llegue el material ENTERO (no a
     que haya activas). Si la card de `?pub=` vino CADUCADA (el Panel la pinto
     activa y caduco antes del toque), sale el mismo aviso y la misma vuelta al
     Panel que el 403 (Anexo A 3 y A 4); si es ajena o no existe, silencio (404
     sin texto nuevo).
     t7-1c (#480): Analíticas identifica una card como "pub:<id>" (el mismo
     `PREFIJO_CARD`) y sus botones de estudiar mandan `?doc=pub:<id>`. Ese `?doc=`
     se trata aquí IGUAL que `?pub=<id>`: la misma lógica de abajo (activa, se
     preselecciona; caducada, aviso y vuelta al Panel; ajena, silencio). Si vienen
     los dos, manda `?pub=`. El `?doc=` de un documento propio no pasa por aquí: lo
     atiende el efecto de arriba, como siempre (un id con el prefijo no está en
     `docs`, así que allá no preselecciona nada). */
  const preselectPubRef = useRef(false);
  useEffect(() => {
    if (!activa) return;
    if (preselectPubRef.current) return;
    const docDeLaUrl = searchParams.get('doc');
    const pubId = searchParams.get('pub')
      || (esIdDeCard(docDeLaUrl) ? docDeLaUrl.slice(PREFIJO_CARD.length) : null);
    if (!pubId || materialDelGrupo === null) return;
    preselectPubRef.current = true;
    const id = `${PREFIJO_CARD}${pubId}`;
    if (entradasDelGrupo.some((e) => e.id === id)) {
      setSelectedDocs([id]);
      setMode((m) => (m === 'flashcards' ? 'adaptive' : m));
      return;
    }
    const caducada = (Array.isArray(materialDelGrupo) ? materialDelGrupo : [])
      .some((g) => (g && Array.isArray(g.cards) ? g.cards : [])
        .some((c) => c && c.id === pubId && c.estado === 'caducada'));
    if (caducada) {
      toast.error(t(`common:errors.${MATERIAL_CADUCADO}`));
      navigate('/dashboard');
    }
  }, [entradasDelGrupo, materialDelGrupo, searchParams, activa, t, navigate]);

  // La carga falló → mismo toast de siempre, una vez por error.
  // pantallas-alumno (C, P1): SALVO cuando no hay lista que pintar (CE12 y CE14): ahi lo dice el
  // recuadro I2 con su "Retry", y el aviso flotante ya no sale.
  useEffect(() => {
    if (error && data !== undefined) toast.error(t('study:center.toasts.load_failed'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Doc recién procesado (T4): el MISMO evento que dispara el toast de
  // NotificationListener refresca esta lista al momento — reacción a un hecho,
  // no polling. Si el payload trae los docs frescos se usan tal cual (cero
  // llamadas extra); si no, un refetch.
  // PAGER-1: la rama sin payload hace `refetch()`, o sea RED. Sólo la raíz
  // activa escucha; la llave del cache se invalida igual por el evento
  // (screenDataCache), así que una raíz dormida no se queda con dato mentiroso:
  // al activarse su entrada ya no está fresca y pide.
  useEffect(() => {
    if (!activa) return undefined;
    const onDocsChanged = (e) => {
      const freshDocs = e.detail?.documents;
      if (Array.isArray(freshDocs)) {
        const readyDocs = sortLibraryDocs(freshDocs.filter(d => d.status === 'ready' || d.status === 'partial'));
        setData(prev => ({ completedSessions: prev?.completedSessions || 0, docs: readyDocs, bloqueados: bloqueadosDe(freshDocs) }));
      } else {
        refetch();
      }
    };
    window.addEventListener('studybuddy:docs-changed', onDocsChanged);
    return () => window.removeEventListener('studybuddy:docs-changed', onDocsChanged);
  }, [setData, refetch, activa]);

  /* t7-1c (#478) -- CADA CARD ES UNA UNIDAD, COMO UN DOCUMENTO (Anexo A). Una
     card entra y sale de la selección igual que un documento propio: sola, con
     otras cards o con documentos propios (en `t7-1b` una card reemplazaba la
     selección entera). La card no ofrece flashcards: si el modo era ése, elegir
     una card lo cae a práctica en el mismo lote que la selección, como antes. Sin
     cards, la multiselección de siempre, línea por línea. */
  const toggleDoc = (id) => {
    if (esIdDeCard(id) && mode === 'flashcards') setMode('adaptive');
    setSelectedDocs(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  // t7-1c (#478) DECLARADO: con dos cards que comparten preguntas (el mismo tema
  // publicado en las dos) este techo cuenta de más, y el de los temas palomeados
  // (`topesPorTema`, abajo) también; el cliente no sabe las `question_ids` de cada
  // card. El servidor arma el pool sin repetir.
  // t7-1b (T5): los conteos leen `todas`, así que el techo de una card es su total.
  const maxQ = todas.filter(d => selectedDocs.includes(d.id)).reduce((sum, d) => sum + (d.question_count || 0), 0);
  const totalFlashcards = todas.filter(d => selectedDocs.includes(d.id)).reduce((sum, d) => sum + (d.flashcard_count || 0), 0);

  /* ── TANDA 32 (#294/#298) — LO QUE DE VERDAD OFRECEN LOS TEMAS PALOMEADOS ──
     `maxQ` y `totalFlashcards` de aquí arriba salen del METADATO del documento
     (`question_count` / `flashcard_count` del catálogo de la pantalla), así que
     son ciegos al filtro: con dos temas palomeados de 11 preguntas seguían
     diciendo 42, y la barra del simulador dejaba pedir un examen que el arranque
     iba a recortar sin avisar.

     El dato ya estaba en mano y no cuesta ni una petición: el catálogo del
     selector (`catalogoDeTemas`) trae `question_count` y `flashcard_count` POR
     TEMA, servidos por la misma clave con la que el arranque recorta el pool. Y
     está garantizado que existe cuando hace falta: la única manera de palomear
     un tema es abrir la ventana, que es justo lo que lo pide.

     DEVUELVE `null` —no un cero— CUANDO NO HAY FILTRO. Un cero sería
     indistinguible de "los temas elegidos no tienen nada", y quien lee esto
     abajo necesita poder caer al total del documento sin ambigüedad.

     LOS DOS MODOS COMPARTEN EL CÁLCULO porque comparten la fuente; cada uno lee
     su campo. `temasElegidos` ya es el del modo activo (`temasPorModo[mode]`),
     así que el filtro de flashcards jamás se cuela al examen ni al revés.

     No se filtra por `selectedDocs`: el catálogo y la selección se reinician en
     el MISMO efecto al cambiar de documentos (ver arriba), así que lo que hay
     aquí siempre corresponde al conjunto vigente — el mismo supuesto sobre el
     que ya trabaja `totalDeTemas`. */
  const topesPorTema = useMemo(() => {
    if (temasElegidos.length === 0 || !catalogoDeTemas) return null;
    const elegidos = new Set(temasElegidos);
    let preguntas = 0;
    let tarjetas = 0;
    for (const doc of catalogoDeTemas.documents || []) {
      for (const tema of doc.topics || []) {
        if (!elegidos.has(tema.name)) continue;
        preguntas += tema.question_count || 0;
        tarjetas += tema.flashcard_count || 0;
      }
    }
    return { preguntas, tarjetas };
  }, [catalogoDeTemas, temasElegidos]);

  /* El TECHO de la perilla de preguntas: el de los temas elegidos si hay filtro,
     el del documento entero si no. Es lo que topan la barra y el cuadro.

     NO es lo que dice el letrero "máximo N" (firma de Carlos): ese sigue leyendo
     `maxQ` porque anuncia lo que el DOCUMENTO tiene, que es un dato que no deja
     de ser cierto por haber filtrado. Y tampoco es lo que valida el arranque
     (`runStartSession`, más abajo): teclear un número mayor a mano sigue
     pasando, y el backend lo ajusta como hoy — la barra y el cuadro ya cierran
     el camino por gesto, que es por donde se llegaba al número imposible. */
  // pantallas-alumno (C, P3): y ahora SI es lo que dice el letrero "max {n}" (la firma de P3 lo
  // cambia), sin pasar nunca del tope de hoy (el del documento entero).
  const topeQ = topesPorTema ? Math.min(topesPorTema.preguntas, maxQ) : maxQ;

  /* Y el de flashcards, que es lo que cuenta la pastilla "N disponibles".

     ── TANDA 32b (#294) — LAS TRES PASTILLAS, UNA SOLA FUENTE ────────────────
     La 32a filtró esta pastilla desde el CATÁLOGO del selector y dejó el
     desglose "conocidas / aún aprendiendo" sin filtrar, porque el resumen de
     progreso llegaba sumado por documento y sin temas. Eso era honesto pero
     partido: con dos temas palomeados la fila decía "20 disponibles · 9/56
     conocidas", dos verdades sobre universos distintos pegadas una al lado de
     la otra.

     Ahora el resumen SÍ sabe de temas, así que con filtro puesto la pastilla de
     disponibles se sirve de ÉL y no del catálogo: las tres salen del mismo
     número y no se pueden contradecir. El catálogo sigue siendo la fuente
     cuando no hay resumen —invitados, y cualquier fallo que lo deje en `null`—,
     que es exactamente el gate que estas pastillas ya tenían.

     SIN SELECCIÓN NO CAMBIA NADA: `topesPorTema` es `null`, se lee
     `totalFlashcards` del catálogo igual que siempre, y el resumen tampoco viaja
     con `topics`. */
  const totalFlashcardsDeTemas = (() => {
    if (!topesPorTema) return totalFlashcards;
    if (flashcardSummary) return flashcardSummary.totals.total;
    return topesPorTema.tarjetas;
  })();

  // ── COS-35 — EL NÚMERO DE PREGUNTAS NUNCA ARRANCA POR ENCIMA DEL MÁXIMO ────
  //
  // El default de fábrica es 10 fijo. Con una selección que sólo tiene 5
  // preguntas (el caso que cazó Carlos con `estado_free`), 10 es un número que
  // el usuario NO puede usar: al picar Iniciar rebota con `questions_max` y hay
  // que corregirlo a mano. El default correcto es `min(10, máximo disponible)`.
  //
  // UNA SOLA REGLA CUBRE LAS DOS MITADES del encargo —el arranque y el cambio de
  // selección—: BAJAR el valor cuando se pasa del máximo. Al montar vale 10 y
  // con maxQ=5 baja a 5; si después la selección encoge, vuelve a bajar. Y como
  // NUNCA sube, jamás deshace un número que el usuario ya bajó a mano (ni lo
  // re-infla cuando la selección crece).
  //
  // `maxQ < 1` no clampea: sin documentos seleccionados —o con documentos sin
  // preguntas— no hay techo que respetar, y 0 tampoco es un valor válido (el
  // slider declara `min={1}`). El valor se queda intacto hasta que exista una
  // selección con preguntas. Esto también cubre el arranque en frío: mientras la
  // lista carga, `docs` está vacía y maxQ es 0.
  //
  // SÓLO EL TECHO EN LAS DEPS, a propósito. Esto reacciona al cambio de
  // SELECCIÓN, no a cada tecla: con `numQInput` en la lista, teclear "20" con un
  // techo de 5 se auto-corregiría letra por letra y el campo quedaría
  // intecleable. El número escrito a mano por encima del máximo sigue teniendo
  // su dueño de siempre — la validación de `runStartSession`, que no se toca.
  //
  // Un `numQInput` a medio teclear (vacío o no numérico) tampoco se pisa.
  //
  // TANDA 32 (#298) — Y EL TECHO AHORA ES `topeQ`, no `maxQ`. Es el MISMO
  // mecanismo con una fuente mejor: palomear dos temas de 11 preguntas cambia
  // `topeQ`, el efecto corre y el número baja solo. Al limpiar la selección
  // `topeQ` vuelve a `maxQ` y —como esta regla NUNCA sube— el número se queda
  // donde el usuario lo dejó, que es exactamente la propiedad que el párrafo de
  // arriba ya defendía.
  useEffect(() => {
    if (topeQ < 1) return;
    const actual = parseInt(numQInput, 10);
    if (isNaN(actual) || actual <= topeQ) return;
    setNumQuestions([topeQ]);
    setNumQInput(String(topeQ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topeQ]);

  // Fetch flashcard progress summary for selected docs
  // PAGER-1: `POST /study/flashcards/progress-summary` — RED, y en el carril
  // `study_user` (150/h). El gate va DELANTE de las condiciones de siempre, que
  // se conservan intactas: dormida no pide y tampoco borra lo que ya tenía.
  useEffect(() => {
    if (!activa) return undefined;
    // t7-1b (T5): `seleccionEsCard` es el cinturón: con una card el modo nunca es
    // flashcards (ver `toggleDoc`), y aun así su id no viaja a este resumen.
    if (mode !== 'flashcards' || selectedDocs.length === 0 || isGuest || seleccionEsCard) {
      setFlashcardSummary(null);
      return undefined;
    }
    let cancelled = false;
    fetchFlashcardSummary(user?.id, selectedDocs, temasElegidos)
      .then(data => { if (!cancelled) setFlashcardSummary(data); })
      .catch(() => { if (!cancelled) setFlashcardSummary(null); });
    return () => { cancelled = true; };
    // TANDA 32b (#294) — Y SE VUELVE A PEDIR AL CAMBIAR LA SELECCIÓN DE TEMAS.
    // Sin esto el resumen se quedaría con los números del documento entero y las
    // pastillas dirían dos verdades distintas a la vez.
    //
    // POR CLAVE Y NO POR ARREGLO (`temasKey`), igual que `selectedKey` hace con
    // los documentos desde la 29: identidad estable POR CONTENIDO. Con el
    // arreglo en las deps, una lista reconstruida con los mismos nombres —que es
    // lo que pasa con `temasPorModo[mode] || []` si alguna vez ese modo no
    // existiera— dispararía una petición por render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedDocs, temasKey, isGuest, user?.id, activa, seleccionEsCard]);

  // Check if any selected docs are missing flashcards or questions.
  // F6.7b — la verdad del TELÉFONO manda: con fila de presencia se cuentan los
  // renglones que de verdad bajaron; sin ella (web SIEMPRE, o un doc que aún no
  // se copia) se cae a los conteos del servidor, que es el cálculo de hoy. Por
  // eso en web esto es byte-idéntico: readLocalPresence() devuelve [] por
  // construcción y el mapa nunca tiene filas.
  //
  // t7-1b (T5): `selectedDocObjs` se queda sobre `docs` A PROPÓSITO. Sólo alimenta
  // el recuadro de regenerar flashcards, la única llamada de esta pantalla a
  // `routes/documents.py`: una card nunca entra ahí (ni el modo flashcards se le
  // ofrece), así que esa llamada queda fuera para ella por construcción. Las
  // guardas de material sí miran `todas`, igual que el aviso del renglón.
  const selectedDocObjs = docs.filter(d => selectedDocs.includes(d.id));
  const hasQuestionsInAll = !selectionBlockedByMaterial(todas, selectedDocs, presenceById, 'adaptive');
  const hasFlashcardsInAll = !selectionBlockedByMaterial(todas, selectedDocs, presenceById, 'flashcards');
  // pantallas-alumno (C): la nota debajo del renglon ya no existe (cambio de Carlos, CE6-a y CE19).
  // Lo que hoy callaba el aviso de "faltan flashcards" (la nota a la vista) ahora es el recuadro de
  // Settings, que dice lo mismo con su boton: con el a la vista, el aviso se sigue callando. El de
  // "faltan preguntas" ya no se calla: con Start apagado es lo unico que explica por que (P5).
  // Docs that are eligible for flashcard regeneration: selected, status ready/partial, has questions, no flashcards.
  const docsMissingFlashcards = selectedDocObjs.filter(
    d => (d.flashcard_count || 0) === 0 && (d.question_count || 0) > 0
  );
  // firma 2 de la parte 1: el invitado tambien tiene el recuadro (el servidor ya se lo permite).
  const recuadroDeFlashcards = mode === 'flashcards' && docsMissingFlashcards.length > 0;
  // firma 1 de la parte 1: los documentos elegidos que el reintento acepta (ver `faltanPreguntas`).
  const docsMissingQuestions = (mode === 'adaptive' || mode === 'exam')
    ? todas.filter((d) => selectedDocs.includes(d.id) && faltanPreguntas(d))
    : [];
  const [genPreguntas, setGenPreguntas] = useState({ active: false, current: 0, total: 0 });

  /* "GENERATE MISSING QUESTIONS" (firma 1 de la parte 1). El MISMO reintento del documento parcial
     que hoy usa el "Retry" del Panel (A10): `POST /documents/{id}/retry`, uno por uno y en orden, 0
     creditos. No termina aqui: el reintento va a la fila y tarda unos minutos.
     - Cada documento que entra sale de la seleccion y de la lista en ese momento (`enviados`).
     - Si entro al menos uno: UN aviso de exito (`queued`) y el aviso de "empezo a cocinar algo"
       que el Panel da al reintentar (`studybuddy:doc-uploaded`), para que el sondeo de avisos lo
       vigile y salga el aviso de siempre cuando el documento quede listo.
     - El que no entra (409 sin intentos, 429, red, servidor) saca el aviso que hoy da el Panel en ese
       caso (`retryErrorToast`, la misma llamada que `Dashboard.js`) y se queda en la lista.
     - Sin red: no manda nada; el boton va apagado y avisa "You need a connection". */
  const handleGenerateMissingQuestions = async () => {
    if (isOffline) { avisarSinRed(t); return; }
    const targets = docsMissingQuestions;
    if (targets.length === 0 || genPreguntas.active) return;
    setGenPreguntas({ active: true, current: 0, total: targets.length });
    let entraron = 0;
    for (let i = 0; i < targets.length; i++) {
      const doc = targets[i];
      setGenPreguntas({ active: true, current: i + 1, total: targets.length });
      try {
        await api.post(`/documents/${doc.id}/retry`);
        entraron += 1;
        setEnviados((prev) => ({ ...prev, [doc.id]: doc.retry_count ?? 0 }));
        setSelectedDocs((prev) => prev.filter((id) => id !== doc.id));
      } catch (err) {
        toast.error(retryErrorToast(err, t, { serverDetailFallback: true }));
      }
    }
    setGenPreguntas({ active: false, current: 0, total: 0 });
    if (entraron > 0) {
      toast.success(t('study:center.missing_questions.toasts.queued'));
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    }
  };

  const handleGenerateMissingFlashcards = async () => {
    if (docsMissingFlashcards.length === 0) return;
    const targets = docsMissingFlashcards;
    setRegenState({ active: true, current: 0, total: targets.length });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const doc = targets[i];
      setRegenState({ active: true, current: i + 1, total: targets.length });
      try {
        const res = await api.post(`/documents/${doc.id}/regenerate-flashcards`);
        const newCount = res.data?.flashcard_count || 0;
        if (newCount > 0) {
          success += 1;
          // Mutación del propio usuario → write-through al cache (setData), para
          // que volver a esta pantalla dentro de la ventana de frescura pinte
          // el conteo ya regenerado.
          setData(prev => prev && ({
            ...prev,
            docs: prev.docs.map(d => d.id === doc.id
              ? { ...d, flashcard_count: newCount, status: res.data?.status || d.status }
              : d),
          }));
        } else {
          failed += 1;
        }
      } catch {
        failed += 1;
      }
    }
    setRegenState({ active: false, current: 0, total: 0 });
    if (success === targets.length) {
      toast.success(t('study:center.missing_flashcards.toasts.all_success', { count: success }));
    } else if (success > 0) {
      toast.warning(t('study:center.missing_flashcards.toasts.partial_success', { success, total: targets.length, failed }));
    } else {
      toast.error(t('study:center.missing_flashcards.toasts.all_failed'));
    }
  };

  // F6.7b (fase 3) — EL CANDADO PRO, evaluado en el ÚNICO momento que le toca:
  // iniciar una sesión de estudio sin señal. No vive en el gate del CTA a
  // propósito — un CTA deshabilitado no vende nada y, peor, sería un CTA que se
  // apaga y se prende con la señal. El botón sigue vivo; quien no tiene Pro ve
  // la pantalla con la propuesta y una salida.
  //
  // Se pide el tier ANTES de navegar (lectura de bodega, milisegundos) y la
  // REGLA DURA de proGate.js manda: sin tier legible, se estudia.
  //
  // ── RPW T7 (P3 / D-G2b) — DOS ARREGLOS, Y NINGUNO CAMBIA LA REGLA DE D-G ───
  //
  // 1. LA SEÑAL. Este gate colgaba de `isExplicitlyOffline()`, que solo es true
  //    con un evento `offline` RECIBIDO — y los eventos son de TRANSICIÓN. Una
  //    app que ARRANCA con el avión ya puesto no ve ninguna, así que el
  //    veredicto se queda en UNKNOWN y **el candado ni se evaluaba**: ese es el
  //    agujero por el que el invitado del S11 arrancaba examen tras examen sin
  //    red (mataba la app, y el proceso nuevo nacía sin veredicto). Ahora
  //    consume `isOfflineForGate(isOffline)`, que respeta al vigía cuando hay
  //    dictamen y, sin él, mira la evidencia que esta pantalla YA tiene: el
  //    `data.offline` del ensamblador de bodega (un fetch que se intentó y no
  //    llegó — evidencia, no sospecha). Es la MISMA señal que pinta el CTA de
  //    aquí al lado; lo que se corrige es que el candado usaba otra.
  //
  // 2. VOLVER NO ES INICIAR. D-G gobierna INICIAR una sesión de estudio sin
  //    señal. Un examen que ya está corriendo en este teléfono no se está
  //    iniciando: se está retomando (regla 0), y la T7-P2 acaba de firmar que el
  //    del invitado SOBREVIVE al kill. Sin esta puerta, el arreglo (1) lo
  //    dejaría encerrado FUERA de su propio examen —sin poder terminarlo ni
  //    dejar que su hora límite lo entregue—, que es lo contrario de lo firmado.
  //    La pregunta la contesta el MISMO resolutor que la entrada va a correr un
  //    instante después (`hasOwnInFlightExam`, solo lectura).
  //
  //    Vale para las DOS variantes, y no por simetría estética: el arreglo (1)
  //    hace que el candado se evalúe en un escenario donde antes ni corría, así
  //    que sin esta puerta también dejaría encerrado fuera de su examen a un
  //    Free que perdió la señal a media sesión. Un arreglo no puede estrenar un
  //    daño nuevo. La regla de negocio de D-G no se mueve un milímetro: iniciar
  //    sin señal sigue siendo de Pro; lo que se corrige es qué cuenta como
  //    iniciar.
  const passesOfflineProGate = async () => {
    if (!IS_APP_BUILD || !isOfflineForGate(isOffline)) return true;
    let tier = null;
    try {
      const m = await import('../lib/warehouse/snapshots');
      tier = await m.readTierSnapshot();
    } catch (e) { /* la duda se resuelve a favor del usuario */ }
    const verdict = evaluateOfflineProGate({
      offline: true, appBuild: IS_APP_BUILD, tier,
    });
    if (verdict.allow) return true;
    if (mode === 'exam' && examRunsLocally()) {
      const m = await import('../lib/brain/examRuntime');
      if (await m.hasOwnInFlightExam(selectedDocs)) return true;
    }
    setProGateVariant(verdict.variant);
    return false;
  };

  // El cuerpo de siempre, intacto: las validaciones, el candado D-G y la
  // navegación. Lo único que cambió es que ya no se le entra directo — pasa por
  // el candado de reentrada de `startSession`, aquí abajo.
  const runStartSession = async () => {
    if (selectedDocs.length === 0) { toast.error(t('study:center.toasts.select_at_least_one')); return; }
    // F6.5b: guard espejo del disabled del CTA (la función única decide).
    if (!availability.available) { toast.error(availabilityMessage()); return; }
    // La sesión no arranca igual que siempre; lo único que cambia es que el
    // toast NO se emite cuando el mensaje inline ya está colgando del renglón
    // del doc culpable (sería el mismo aviso dos veces, y el inline es mejor:
    // dice cuál documento).
    if (mode === 'flashcards' && !hasFlashcardsInAll) {
      if (!recuadroDeFlashcards) toast.error(t('study:center.toasts.no_flashcards'));
      return;
    }
    if ((mode === 'exam' || mode === 'adaptive') && !hasQuestionsInAll) {
      toast.error(t('study:center.toasts.no_questions'));
      return;
    }
    // Validate exam inputs on start
    if (mode === 'exam') {
      const errors = {};
      const numQ = parseInt(numQInput);
      const tl = parseInt(timeLimitInput);
      if (!numQInput.trim() || isNaN(numQ) || numQ < 1) {
        errors.numQuestions = !numQInput.trim() ? t('study:center.validation.questions_required') : t('study:center.validation.questions_min');
      } else if (numQ > topeQ) {
        // pantallas-alumno (C, P3): el maximo del aviso es el MISMO que dice el letrero.
        errors.numQuestions = t('study:center.validation.questions_max', { max: topeQ });
      }
      if (!timeLimitInput.trim() || isNaN(tl) || tl < 1) {
        errors.timeLimit = t('study:center.validation.time_range');
      } else if (tl > 360) {
        errors.timeLimit = t('study:center.validation.time_range');
      }
      if (Object.values(errors).some(Boolean)) {
        setValidationErrors(errors);
        return;
      }
    }
    // Último paso antes de navegar: el candado D-G. Va aquí, después de todas
    // las validaciones, para que nadie vea la pantalla de venta por un error de
    // configuración que igual habría detenido la sesión.
    if (!(await passesOfflineProGate())) return;
    const finalNumQ = parseInt(numQInput) || numQuestions[0];
    const finalTimeLimit = parseInt(timeLimitInput) || timeLimit[0];
    // F6.6a: `offline` viaja en el state de navegación — es la señal ÚNICA
    // que Flashcards usa para D-H (controles de edición) y el reset
    // deshabilitados sin red. En web isOffline jamás es true (data.offline
    // solo lo escriben los ensambladores nativos): campo inerte, render
    // byte-idéntico.
    // TANDA 29 (#273) — `topics` viaja SÓLO si hay algo palomeado, y por eso es
    // un `...spread` condicional y no un campo con default: sin selección la
    // llave NO EXISTE en el `state` de navegación, así que el cuerpo que las tres
    // pantallas de destino postean es byte-idéntico al de antes de esta tanda.
    // Un `topics: []` sería equivalente para el backend, pero no para el diff.
    //
    // TANDA 29b (#271 enmendada) — Y EN ADAPTATIVA NO VIAJA NUNCA. El renglón ya
    // no existe en ese modo, así que `temasPorModo.adaptive` no puede llenarse
    // desde la pantalla; el gate se escribe igual porque es aquí donde vive la
    // regla, no en el render. Sin él, cualquier camino futuro que poblara ese
    // estado (un enlace profundo, una restauración) le colaría un filtro al modo
    // que precisamente NO debe llevarlo.
    const mandaTemas = mode !== 'adaptive' && temasElegidos.length > 0;
    // t7-1b (T5): con una card, `documentIds` es su documento y `publicacionId`
    // viaja en el `state` para que la pantalla de destino lo mande en su POST.
    // Mismo `spread` condicional que `topics`: con un documento propio la llave
    // NO EXISTE y el `state` es el de siempre.
    // t7-1c (#478): con cards, `documentIds` son los propios elegidos y el
    // documento de cada card (`documentosConCards`). Con UNA card viaja
    // `publicacionId` (la cadena de t7-1b); con dos o más, `publicacionIds` (la
    // lista, en el orden en que se eligieron); nunca las dos. Sin cards, ninguna de
    // las dos llaves existe y `documentIds` es `selectedDocs` tal cual.
    // t7-3 (A4): en la app el estudio corre en el telefono sobre la bodega, donde la card ES el
    // documento `pub:<id>`: viajan los ids locales tal cual (las cards van en `publicacion_id`
    // solo para la web).
    const conCards = cardsElegidas.length > 0 && !IS_APP_BUILD;
    const config = {
      documentIds: conCards ? documentosConCards() : selectedDocs, numQuestions: finalNumQ, timeLimit: finalTimeLimit,
      offline: isOffline,
      ...(mandaTemas ? { topics: temasElegidos } : {}),
      ...(cardsElegidas.length === 1 ? { publicacionId: cardsElegidas[0].publicacion_id } : {}),
      ...(cardsElegidas.length > 1 ? { publicacionIds: cardsElegidas.map((c) => c.publicacion_id) } : {}),
    };
    trackEvent('study_session_started', { mode, document_count: selectedDocs.length, num_questions: finalNumQ });
    // RPW T5 (P2b) — EL INTENTO DE INICIAR VIAJA CON LA NAVEGACIÓN, y es de UN
    // SOLO USO. `location.state` sobrevive al reload y se replica cuando el
    // usuario vuelve con el botón Atrás a esta entrada del historial, así que la
    // pantalla del examen no podía distinguir "acabo de picar Iniciar" de "volví
    // a una entrada que ya se usó" — y arrancaba un examen nuevo en los dos
    // casos. Con esta marca, la pantalla consume el intento la primera vez y las
    // demás entradas sólo CONSULTAN. Sólo el examen la necesita: adaptive y
    // flashcards no sortean nada al entrar.
    const startToken = (window.crypto?.randomUUID?.() || `t${Date.now()}-${Math.random()}`);
    // FLASH-3 (pieza 1) — la firma del empujón, JUSTO ANTES del `navigate`. El
    // token viaja solo cuando existe de verdad: los otros dos modos no sortean
    // nada al entrar y no lo llevan en su `state`.
    logNavPush(
      mode === 'exam' ? '/study/exam' : mode === 'flashcards' ? '/study/flashcards' : '/study/adaptive',
      mode === 'exam' ? startToken : null,
    );
    if (mode === 'exam') navigate('/study/exam', { state: { ...config, startToken } });
    else if (mode === 'flashcards') navigate('/study/flashcards', { state: config });
    else navigate('/study/adaptive', { state: config });
  };

  // ── FLASH-3 (pieza 1) — LA PUERTA ÚNICA DE "INICIAR" ────────────────────────
  //
  // Lo que este envoltorio garantiza: entre el primer tap y el final del
  // arranque no cabe un segundo. El candado es el REF, no el estado — ver el
  // letrero de su declaración.
  //
  // CUÁNDO SE ABRE OTRA VEZ: cuando el arranque TERMINA, y terminar es las dos
  // cosas — haber navegado o haber fallado. Por eso va en un `finally` y no en
  // cada `return`: una validación que rebota tiene que devolver el botón al
  // usuario de inmediato (si no, un número mal tecleado dejaría la pantalla
  // muerta), y tras el `navigate` esta pantalla se desmonta, así que reabrir ahí
  // no le regala nada a nadie. Un click fantasma que llegue DESPUÉS del
  // `navigate` ya no es asunto de esta puerta: lo consume la pieza 2, que es
  // justo la defensa en profundidad para la segunda navegación que sí llega.
  //
  // El `setStarting` de ida y el de vuelta caen en el MISMO lote cuando el
  // arranque rebota por una validación síncrona: React los batchea, el valor
  // final es el de partida y no hay re-render — las rutas de validación quedan
  // exactamente como estaban.
  const startSession = async () => {
    if (startingRef.current) return;   // reentrada: NO-OP silencioso
    startingRef.current = true;
    setStarting(true);
    try {
      await runStartSession();
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  /* COS-I — EL SWIPE ENTRE PESTAÑAS. Vecinas: Inicio a la izquierda, Analíticas
     a la derecha.

     LA ÚNICA CAPA DE ESTA PANTALLA es el candado Pro sin señal (D-G). Los DOS
     sliders del examen no se apagan con la capa —siguen vivos, que es todo el
     punto—: el hook los excluye como FUENTE, así que un dedo que nace sobre
     ellos arrastra el slider y jamás cambia de pestaña
     (`TAB_SWIPE_EXCLUDED_SOURCES`).

     PAGER-2 — EL CENSO SE QUEDA AQUÍ, EL GESTO NO: lo monta UNA vez el carril
     (`components/PagerRaices.js`) y de aquí sólo sube este booleano. La barra de
     acción fija sigue siendo HERMANA de este contenedor, así que sigue sin
     recibir el gesto; lo que cambió es que ahora tampoco se queda quieta —viaja
     dentro de la pane, con su pantalla, que es lo que el usuario espera al ver
     las dos pantallas deslizarse juntas. */
  const capaEncima = !!proGateVariant;
  useReportarCapa(capaEncima);

  // Resumen compacto de la CTA fija (mobile): absorbe el "N documento(s)
  // seleccionado(s)" y refleja el estado real de la configuración por modo.
  //
  // TANDA 18 (#208): se calcula ANTES de la puerta de abajo porque la barra ya
  // no espera al dato. Es aritmética pura sobre el estado que la pantalla ya
  // tiene, así que adelantarla no adelanta ni un efecto.
  //
  // pantallas-alumno (C, P2): el resumen dice lo que DE VERDAD hay en las cajas: un cuadro vacio
  // se dice con el guion de hoy (`settings.time_empty`) y un numero fuera de rango se dice tal cual,
  // nunca el ultimo valor bueno de la barra.
  const ctaSummaryParts = [];
  if (selectedDocs.length === 0) {
    ctaSummaryParts.push(t('study:center.docs_selected', { count: 0 }));
  } else {
    ctaSummaryParts.push(t('study:center.cta_summary.docs', { count: selectedDocs.length }));
    ctaSummaryParts.push(t(`study:center.modes.${mode}.name`));
    if (mode === 'exam') {
      const vacio = t('study:center.settings.time_empty');
      ctaSummaryParts.push(t('study:center.cta_summary.exam_config', {
        questions: numQInput.trim() || vacio,
        time: timeLimitInput.trim() || vacio,
      }));
    } else if (mode === 'flashcards' && totalFlashcardsDeTemas > 0) {
      ctaSummaryParts.push(t('study:center.count_available', { count: totalFlashcardsDeTemas }));
    }
  }
  const ctaSummary = ctaSummaryParts.join(', ');

  /* pantallas-alumno (C, P5): "Start studying" APAGADO (aria-disabled, el patron de la casa: se ve
     al 45 %, no navega y avisa) mientras haya un error en Settings o un documento elegido sin
     preguntas en Adaptive o Exam. Al tocarlo: con un documento sin preguntas, el aviso de hoy; con
     un error en las cajas, nada (el error ya esta a la vista). Sin documentos, lo de hoy (apagado
     de verdad). En Flashcards con un documento sin flashcards se queda como hoy. */
  const hayErrorEnSettings = mode === 'exam' && Object.values(validationErrors).some(Boolean);
  const faltanPreguntasAlElegido = (mode === 'exam' || mode === 'adaptive') && selectedDocs.length > 0 && !hasQuestionsInAll;
  const startApagadoSuave = hayErrorEnSettings || faltanPreguntasAlElegido;
  const alTocarStartApagado = () => {
    if (faltanPreguntasAlElegido) toast.error(t('study:center.toasts.no_questions'));
  };
  // FLASH-3 (pieza 1): mientras arranca, el boton TRABAJA (texto invisible, giro encima, mismo ancho;
  // 4.1 del encargo) y no se puede tocar: `trabajando` del `Boton` lo deshabilita igual que antes.
  const startDisabled = selectedDocs.length === 0 || !availability.available;

  // pantallas-alumno (C, P1): el "Retry" del recuadro I2 vuelve a pedir lo mismo que hoy.
  const [reintentando, setReintentando] = useState(false);
  const reintentarCarga = () => {
    setReintentando(true);
    Promise.resolve()
      .then(() => refetch())
      .catch(() => {})
      .finally(() => setReintentando(false));
  };

  // CE16: la lista larga se desplaza por dentro, y un degradado de 48 avisa que hay mas abajo.
  const listaRef = useRef(null);
  const [hayMasAbajo, setHayMasAbajo] = useState(false);
  const medirLista = () => {
    const el = listaRef.current;
    if (!el) return;
    setHayMasAbajo(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  };
  useEffect(() => {
    medirLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todas.length, bloqueados.length, loading, error]);
  // S1: el alto de la lista ya no es fijo (llena lo que sobra), asi que el degradado se vuelve a decidir cuando
  // cambia el tamano de la ventana o de la caja.
  useEffect(() => {
    const el = listaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(() => medirLista());
    observador.observe(el);
    return () => observador.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todas.length > 0 || bloqueados.length > 0, loading, error]);

  /* centro-espacio (orden de Carlos del 20-oct; LAPIDA de los escalones de smoke-alumno, S1 segunda vuelta): la
     pagina ya no se aprieta por escalones. Se acomoda sola con CSS (`acomodoDelCentro.js`): la caja de documentos se
     lleva lo que sobra y solo si ni dos renglones caben la pagina crece.
     Lo unico que se mide es el aire de abajo en el celular: la pagina lleva lo que la barra fija le tapa MAS 12
     (`AIRE_SOBRE_LA_BARRA`), asi Settings queda a 12 de la barra en cualquier pantalla (la barra crece con su
     aviso). Sin barra a la vista (desde 768) o sin medidas (jsdom), `null`: la clase de siempre. */
  const [holguraMedida, setHolguraMedida] = useState(null);
  const [vueltaDeMedida, setVueltaDeMedida] = useState(0);
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    let medida = null;
    const barra = document.querySelector('[data-testid="study-center-cta-bar"]');
    const scrollport = scrollportDeLaRaiz();
    if (barra && scrollport) {
      const rb = barra.getBoundingClientRect();
      const rs = scrollport.getBoundingClientRect();
      if (rb.height > 0 && rs.height > 0) {
        const pie = parseFloat(window.getComputedStyle(scrollport).paddingBottom) || 0;
        medida = Math.max(0, Math.round(rs.bottom - pie - rb.top + AIRE_SOBRE_LA_BARRA));
      }
    }
    if (medida !== holguraMedida) setHolguraMedida(medida);
  }, [scrollportDeLaRaiz, holguraMedida, vueltaDeMedida, ready, loading, extraDelAviso]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const otraVez = () => setVueltaDeMedida((n) => n + 1);
    window.addEventListener('resize', otraVez);
    window.addEventListener('orientationchange', otraVez);
    let observador = null;
    const barra = document.querySelector('[data-testid="study-center-cta-bar"]');
    if (barra && typeof ResizeObserver !== 'undefined') { observador = new ResizeObserver(otraVez); observador.observe(barra); }
    return () => {
      window.removeEventListener('resize', otraVez);
      window.removeEventListener('orientationchange', otraVez);
      if (observador) observador.disconnect();
    };
  }, [ready, loading]);
  // tutor-1 (E): en Exam, en el celular, la separacion de la pagina de 8 a 4 (`APRETADO_EN_EXAM`).
  const apretadoDeLaPagina = mode === 'exam' ? ` ${APRETADO_EN_EXAM.pagina}` : '';
  const estiloDeLaPagina = holguraMedida != null
    ? { paddingBottom: holguraMedida }
    : (extraDelAviso > 0 ? { paddingBottom: COLCHON_DEL_CENTRO_PX + extraDelAviso } : undefined);

  /* TANDA 18 (#208) y TANDA 21 (#212): LA BARRA DE ACCIÓN ES DE PLANTA, Y MUEBLE DE SU HOJA.
     El botón de empezar y su descripción se montan desde el PRIMER pintado, en su lugar definitivo:
     cuando el dato aterriza sólo cambia el TEXTO, en el mismo nodo. Se guarda en una variable y las
     dos ramas de abajo la pintan en la MISMA posición de su fragmento, así que React la reconcilia
     en su lugar en vez de remontarla.
     Dentro del carril la barra cuelga del MARCO de esta hoja (portal), no de la pantalla: entra
     deslizándose con su hoja. Sin carril (web, arnés) se ancla a la pantalla como siempre.
     pantallas-alumno (C): la barra es la pieza del alumno (`BarraFijaDeAcciones`, solo abajo de
     768, apoyada sobre la barra de abajo). Si el material no esta en el telefono, en lugar del
     resumen va el aviso gris con la nube tachada y Start se apaga (CE10-a).
     centro-espacio-2 (orden de Carlos del 20-oct): el resumen ("0 documents selected" y, con
     documentos elegidos, su resumen) ya NO se ve; la barra se ajusta al boton y ese alto se lo
     lleva la pagina. El renglon solo aparece con el aviso de la nube, que explica por que el boton
     no sirve. El nodo se queda montado y escondido (el MISMO nodo en los dos casos, #591; su texto
     sigue al dia y su alto, 0, no suma nada al colchon). */
  const marcoDeLaHoja = useMarcoDeLaPane();
  const recuadroDeAccion = (
    <BarraFijaDeAcciones
      testid="study-center-cta-bar"
      anclaje={marcoDeLaHoja ? ANCLAJE.MUEBLE : ANCLAJE.VIEWPORT}
    >
      {/* La barra es HERMANA del contenedor de la pantalla, así que el noselect de la pantalla no
          la alcanza por herencia: este envoltorio se lo da. */}
      <div className={IS_APP_BUILD ? 'app-noselect-text' : undefined} data-testid="cta-bar-inner">
        {/* t7-3 remate 8 (#591, C116): el aviso de por que el boton esta apagado parte renglon y se
            lee entero; el resumen corto conserva su renglon. Es el MISMO nodo en los dos casos. */}
        <p className={availability.available
          ? 'hidden'
          : 'text-[13px] leading-[18px] text-[#9A9A9A] mb-2 break-words text-center'} data-testid="cta-summary" ref={refDelAviso}>
          {!availability.available
            ? <><CloudOff className="inline-block w-3.5 h-3.5 mr-1.5 -mt-0.5 align-middle" aria-hidden="true" />{availabilityMessage()}</>
            : ctaSummary}
        </p>
        {/* El CTA de mobile, con el mismo `starting` que su gemelo de escritorio (FLASH-3, pieza 1). */}
        <Boton onClick={startSession} disabled={startDisabled} trabajando={starting} apagadoSuave={startApagadoSuave}
          alTocarApagado={alTocarStartApagado} alto="barra" aLoAncho icono={Play}
          className="!rounded-[14px] !text-[16px]" testid="start-session-bar-btn">
          {t('study:center.start_btn')}
        </Boton>
      </div>
    </BarraFijaDeAcciones>
  );

  const barraDeAccion = marcoDeLaHoja
    ? createPortal(recuadroDeAccion, marcoDeLaHoja)
    : recuadroDeAccion;

  /* LA PUERTA DEL DATO, PARTIDA EN DOS (TANDA 18, #208; TANDA 22, #214; t7-1b, T5).
     `!ready`: i18n todavía sin sus namespaces; sin barra (pintaría llaves crudas).
     Cargando, o sin dato y sin error (`data === undefined`: una pane que nace dormida no levanta
     `loading`), o sin documentos propios mientras el material del grupo va en camino: la silueta,
     ya con la barra montada. Con la carga caída y sin dato, abajo: el recuadro I2 (P1).
     pantallas-alumno (C, CE11): la silueta copia el acomodo nuevo (`EsqueletoDelCentro`). */
  if (!ready) return <EsqueletoDelCentro />;
  // Cotejo con el PDF (CE11 p33): con las traducciones listas, los encabezados y Start se ven desde el principio.
  const textosDelEsqueleto = {
    titulo: t('study:center.title'),
    documentos: t('study:center.documents.heading'),
    settings: t('study:center.settings.heading'),
    start: t('study:center.start_btn'),
    startCorto: t('study:center.start_btn_short'),
  };
  if (loading || (data === undefined && !error) || (materialPendiente && docs.length === 0 && !error)) return (
    <>
      {/* S1: la holgura va en la pagina del esqueleto (no en un envoltorio), para que llene el chasis igual que la
          pagina de verdad y al llegar el dato nada brinque. */}
      <EsqueletoDelCentro textos={textosDelEsqueleto} clase={HOLGURA_DEL_CENTRO} estilo={estiloDeLaPagina} />
      {barraDeAccion}
    </>
  );

  /* pantallas-alumno (C, P7): EL AVANCE DE FLASHCARDS ES EL MISMO EN TODOS LOS ANCHOS (el del
     celular: chips y "View per-document breakdown"). La linea "Total: ..." de computadora se va.
     El desglose dice por documento su nombre (con "...") y DEBAJO "20/52 known, 32 still learning"
     completo (CE3-b). Los textos son las llaves de los chips que ya existen. */
  const avanceDeUnDocumento = (known, total) => (
    <>
      <span className="text-[#4ADE80]" data-testid="fc-summary-known">
        {t('study:center.flashcard_chips.known', { known, total })}
      </span>
      {', '}
      <span className="text-[#F7A06A]" data-testid="fc-summary-learning">
        {t('study:center.flashcard_chips.learning', { count: total - known })}
      </span>
    </>
  );

  /* TANDA 32 (#295): EL BOTÓN DE TEMAS, UNA SOLA VEZ (se pinta en Exam y en Flashcards).
     pantallas-alumno (C): la pieza del alumno (`BotonDeTemas`). Apagado (45 %) sin documento o con el
     material fuera del aparato (#407); al tocarlo, el aviso de hoy de cada caso. */
  const totalDelBoton = Math.max(totalDeTemas, temasElegidos.length);
  const botonDeTemas = (
    <BotonDeTemas
      testid="topics-btn"
      conTemas={temasElegidos.length > 0}
      texto={temasElegidos.length === 0
        ? t('study:center.topics.modal_title')
        : t('study:center.settings.topics_selected', {
            // smoke-alumno, S3: el sustantivo cuenta el TOTAL ("1 of 23 topics", "1 of 1 topic"), asi que la forma
            // del plural la decide el total y los elegidos viajan aparte.
            n: temasElegidos.length,
            total: totalDelBoton,
            count: totalDelBoton,
          })}
      onClick={abrirSelectorDeTemas}
      apagadoSuave={temasApagados}
      alTocarApagado={() => {
        // #407: sin documento manda su banner de siempre; sólo con documento habla la puerta.
        if (selectedDocs.length === 0) { toast.info(t('study:center.toasts.select_doc_for_settings')); return; }
        if (materialFuera) toast.info(t('common:offline.pool_not_ready'));
      }}
    />
  );

  /* series-7 (#486) -- FLASHCARDS APAGADO POR UNA CARD, Y SU BANNER (CE5-a y CE5-b). */
  const apagadoPorCard = (id) => id === 'flashcards' && seleccionEsCard;
  const avisarCardsSinFlashcards = () => toast.info(t('study:center.toasts.cards_sin_flashcards'));
  // P8: el aviso de hoy al tocar un documento bloqueado por cambio de plan.
  const avisarBloqueado = () => toast.error(t('study:errors.document_locked'));

  /* CE1-a y CE18: "Recommended" solo con menos de 6 sesiones completas; si no sale, su lugar se
     queda reservado (invisible) y la tarjeta no cambia de alto.
     Remate (R4, decision 12): con una ventana de menos de 340 px de ancho la pildora no cabe en la tarjeta,
     asi que ahi no se pinta y su lugar se queda reservado igual (la tarjeta no cambia de alto). */
  const chipRecomendado = (
    <ChipVerde testid="recommended-chip">{t('study:center.recommended_badge')}</ChipVerde>
  );
  const chipDeAdaptive = completedSessions < 6
    ? <ChipVerde testid="recommended-chip" className="max-[340px]:invisible">{t('study:center.recommended_badge')}</ChipVerde>
    : <span className="invisible" aria-hidden="true">{chipRecomendado}</span>;

  const sinDocsElegidos = selectedDocs.length === 0;
  const noCargo = Boolean(error) && data === undefined;

  // firma 1 de la parte 1: el recuadro de "Generate Missing Questions" (Adaptive y Exam).
  const recuadroDePreguntas = docsMissingQuestions.length > 0 ? (
    <RecuadroDeLoQueFalta
      testid="missing-questions-warning"
      texto={t('study:center.missing_questions.warning', { count: docsMissingQuestions.length })}
      textoBoton={t('study:center.missing_questions.generate_btn')}
      trabajando={genPreguntas.active}
      textoTrabajando={t('study:center.missing_flashcards.generating_btn', { current: genPreguntas.current, total: genPreguntas.total })}
      onGenerar={handleGenerateMissingQuestions}
      sinRed={isOffline}
      alTocarApagado={() => avisarSinRed(t)}
    />
  ) : null;

  const renglonDe = (doc, bloqueado) => (
    <div key={doc.id} data-testid={`select-doc-${doc.id}`}>
      <RenglonDeDocumento
        testid={`renglon-doc-${doc.id}`}
        nombre={doc.filename}
        claveTipo={doc.delGrupo ? 'pdf' : claveDelTipo(doc)}
        publicacion={Boolean(doc.delGrupo)}
        chip={!bloqueado && doc.status === 'partial' ? (
          <ChipDeEstado tono="naranja">
            {doc.question_count === 0 ? t('study:center.documents.partial_no_questions') : t('study:center.documents.partial_no_flashcards')}
          </ChipDeEstado>
        ) : null}
        conteo={doc.delGrupo ? `${doc.question_count}q` : `${doc.question_count || 0}q ${doc.flashcard_count || 0}f`}
        marcado={!bloqueado && selectedDocs.includes(doc.id)}
        onAlternar={() => toggleDoc(doc.id)}
        bloqueado={bloqueado}
        alTocarBloqueado={avisarBloqueado}
      />
    </div>
  );

  return (
    <>
    {/* T2-C4 (segundo smoke): CERO SELECCIÓN AZUL EN TODA LA PANTALLA (app). La clase se hereda
        hacia adentro en WebKit; los dos cuadros de numero del examen la recuperan con su estilo
        propio (`SELECCION_DE_TEXTO`). La barra de acción no cuelga de este div: lleva su propio
        envoltorio con la misma clase.
        pantallas-alumno (C): el acomodo del documento (contenido hasta 1200, dos columnas desde
        1024) y la holgura de la barra fija en el celular (post-t7-1 M4: crece con los renglones de
        mas del aviso de la barra). */}
    <div className={`${PAGINA_DEL_CENTRO}${apretadoDeLaPagina} ${HOLGURA_DEL_CENTRO} animate-fade-in-up${IS_APP_BUILD ? ' app-noselect-text' : ''}`} data-testid="study-center-page"
      data-vuelta={vueltaDeMedida}
      style={estiloDeLaPagina}>
      {/* centro-espacio: sin el subtitulo (la llave `study:center.subtitle` se queda en los locales sin uso). */}
      <div>
        <h1 className={TITULO_DEL_CENTRO} data-testid="study-center-title">{t('study:center.title')}</h1>
      </div>

      {/* CE1-a: los tres modos, la MISMA pieza en los tres anchos (`TarjetaDeModo`) y la fila las
          estira al mismo alto. t7-1b (T5) y series-7 (#486): con una publicacion elegida, Flashcards
          va apagada y al tocarla explica por que (CE5-a y CE5-b). */}
      <FilaDeModos testid="study-center-modes" className="mt-1">
        {MODES_VISUAL.map((m) => (
          <TarjetaDeModo
            key={m.id}
            testid={`mode-chip-${m.id}`}
            icono={m.icon}
            nombre={t(`study:center.modes.${m.id}.name`)}
            descripcion={t(`study:center.modes.${m.id}.desc`)}
            chip={m.id === 'adaptive' ? chipDeAdaptive : null}
            elegida={mode === m.id}
            apagada={apagadoPorCard(m.id)}
            onElegir={() => setMode(m.id)}
            alTocarApagada={avisarCardsSinFlashcards}
          />
        ))}
      </FilaDeModos>

      <div className={mode === 'exam' ? `${REJILLA_DEL_CENTRO} ${APRETADO_EN_EXAM.rejilla}` : REJILLA_DEL_CENTRO}>
        {/* Select Documents. t7-1b (T5): las cards del grupo arriba, en el orden del servidor, y
            despues los documentos propios; P8: al final los bloqueados por cambio de plan. */}
        <section className={`${RECUADRO_DEL_CENTRO} ${RECUADRO_DE_DOCUMENTOS}${mode === 'exam' ? ` ${APRETADO_EN_EXAM.documentos}` : ''}`} data-testid="study-center-docs">
          <h2 className={TITULO_DEL_RECUADRO}>
            <FileText className="w-[18px] h-[18px] shrink-0 text-[#F27A2E]" aria-hidden="true" />
            {t('study:center.documents.heading')}
          </h2>
          {noCargo ? (
            <RecuadroDeEstado
              className="mt-4"
              tipo="no_cargo"
              testid="study-center-no-cargo"
              titulo={t('dashboard:panel.no_cargaron_titulo')}
              texto={t('dashboard:panel.no_cargaron_texto')}
              accion={{ texto: t('dashboard:doc_card.retry_btn'), onClick: reintentarCarga, icono: RotateCw, trabajando: reintentando }}
            />
          ) : todas.length === 0 && bloqueados.length === 0 ? (
            <div className="mt-4 rounded-[14px] border border-dashed border-[#2C2C2C] px-5 py-10 flex flex-col items-center text-center" data-testid="study-center-empty">
              <p className="text-[14px] leading-5 text-[#9A9A9A]">{t('study:center.documents.empty_body')}</p>
              <Boton className="mt-4" onClick={() => navigate('/dashboard')} testid="study-center-empty-cta">
                {t('study:center.documents.empty_cta')}
              </Boton>
            </div>
          ) : (
            <div className={mode === 'exam' ? `${CAJA_DE_LA_LISTA} ${APRETADO_EN_EXAM.lista}` : CAJA_DE_LA_LISTA}>
              <div ref={listaRef} onScroll={medirLista} className={LISTA_DEL_CENTRO} data-testid="study-center-doc-list">
                {todas.map((doc) => renglonDe(doc, false))}
                {bloqueados.map((doc) => renglonDe(doc, true))}
              </div>
              {hayMasAbajo ? <div className={DEGRADADO_DE_LA_LISTA} aria-hidden="true" data-testid="study-center-doc-list-degradado" /> : null}
            </div>
          )}
        </section>

        {/* Settings: lo de cada modo y, en tablet y computadora, el pie con el boton de empezar. En
            computadora va pegado arriba al desplazar la pagina. */}
        <section className={`${RECUADRO_DEL_CENTRO} ${SETTINGS_DEL_CENTRO} ${PEGADO_EN_COMPU}${mode === 'exam' ? ` ${APRETADO_EN_EXAM.recuadro}` : ''}`} data-testid="study-center-settings">
          <h2 className={mode === 'exam' ? `${TITULO_DEL_RECUADRO} ${APRETADO_EN_EXAM.titulo}` : TITULO_DEL_RECUADRO}>{t('study:center.settings.heading')}</h2>
          <div className={mode === 'exam' ? `${CUERPO_DE_SETTINGS} ${APRETADO_EN_EXAM.cuerpo}` : CUERPO_DE_SETTINGS}>
          {mode === 'adaptive' && (
            <>
              {/* Tablet y computadora: el parrafo completo con su diana. */}
              <div className="max-md:hidden flex items-start gap-2 text-[13px] leading-5 text-[#C8C8C8]" data-testid="adaptive-description">
                <Target className="w-4 h-4 text-[#F27A2E] shrink-0 mt-0.5" aria-hidden="true" />
                <p>{t('study:center.adaptive_description')}</p>
              </div>
              {/* CE1-b, celular: un renglon con "..." y el boton de 44 que la abre completa y cambia a
                  una flecha para cerrarla. centro-espacio: 12 arriba y abajo (44 con un renglon) y todo arriba, asi la
                  diana y la "i" quedan centradas con el PRIMER renglon, cerrado y abierto. */}
              <button type="button" onClick={() => setAdaptiveInfoOpen(o => !o)}
                aria-expanded={adaptiveInfoOpen}
                aria-label={t('study:center.adaptive_info_aria')}
                className="md:hidden min-h-[44px] py-3 flex items-start gap-2 text-[13px] leading-5 text-[#C8C8C8] text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-lg"
                data-testid="adaptive-description-compact">
                <Target className="w-4 h-4 text-[#F27A2E] shrink-0 mt-0.5" aria-hidden="true" />
                <p className={`flex-1 min-w-0 ${adaptiveInfoOpen ? '' : 'line-clamp-1'}`}>{t('study:center.adaptive_description')}</p>
                <span className="w-11 h-11 -my-3 -mr-2 shrink-0 flex items-center justify-center text-[#9A9A9A]" aria-hidden="true">
                  {adaptiveInfoOpen ? <ChevronUp className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                </span>
              </button>
              {recuadroDePreguntas}
            </>
          )}
          {mode === 'exam' && (
            <>
              {/* CE2-e: sin documento, cajas y barras al 45 % y al tocarlas el aviso de hoy. El
                  abuelo escucha el toque; el padre apaga los eventos de adentro.
                  centro-espacio-2: abajo de 1024 cada barra va al lado de su caja, en el mismo renglon
                  (`barra` de `CajaDeNumero`); desde 1024, apiladas como antes. */}
              <div onClick={() => { if (sinDocsElegidos) toast.info(t('study:center.toasts.select_doc_for_settings')); }}>
                <div className={sinDocsElegidos ? 'pointer-events-none' : ''}>
                  <div className="grid grid-cols-2 gap-2 md:gap-3" data-testid="exam-knobs-row">
                    <CajaDeNumero
                      id="num-questions"
                      testid="num-questions-knob"
                      etiqueta={t('study:center.settings.questions_label')}
                      dato={topeQ ? t('study:center.settings.max_hint', { max: topeQ }) : t('study:center.settings.max_hint_empty')}
                      valor={numQInput}
                      onCambio={(v) => handleNumQInputChange({ target: { value: v } })}
                      error={validationErrors.numQuestions}
                      apagada={sinDocsElegidos}
                      inputProps={{ 'data-testid': 'num-questions-input', onFocus: selectAllOnFocus, onBlur: handleNumQInputBlur, style: SELECCION_DE_TEXTO }}
                      anchoDeLaCaja="w-16"
                      barra={(
                        <BarraDeslizante etiqueta={t('study:center.settings.questions_label')}
                          min={1} max={Math.max(topeQ, 1)} valor={numQuestions[0]}
                          onCambio={(n) => handleNumQSlider([n])} apagada={sinDocsElegidos} />
                      )}
                    />
                    <CajaDeNumero
                      id="time-limit"
                      testid="time-limit-knob"
                      etiqueta={t('study:center.settings.time_limit_label')}
                      unidad={t('study:center.settings.time_unit')}
                      valor={timeLimitInput}
                      onCambio={(v) => handleTimeLimitInputChange({ target: { value: v } })}
                      error={validationErrors.timeLimit}
                      apagada={sinDocsElegidos}
                      inputProps={{ 'data-testid': 'time-limit-input', onFocus: selectAllOnFocus, style: SELECCION_DE_TEXTO }}
                      anchoDeLaCaja="w-20"
                      barra={(
                        <BarraDeslizante etiqueta={t('study:center.settings.time_limit_label')}
                          min={0} max={TIME_STEPS.length - 1} valor={nearestTimeStepIndex(timeLimit[0])}
                          onCambio={(n) => handleTimeLimitSlider([n])} apagada={sinDocsElegidos} />
                      )}
                    />
                  </div>
                </div>
              </div>
              {recuadroDePreguntas}
              {botonDeTemas}
            </>
          )}
          {mode === 'flashcards' && (
            <>
              {/* P7: chips "{n} available" (neutro), "{known}/{total} known" (verde) y "{n} still
                  learning" (naranja suave). F6.8 T3 (bug B): cada chip se gatea por su propia
                  fuente; el invitado no recibe el resumen y no ve known ni still learning. */}
              {!sinDocsElegidos && (totalFlashcardsDeTemas > 0 || flashcardSummary) && (
                <div className="flex flex-wrap gap-1.5" data-testid="flashcard-chips">
                  {totalFlashcardsDeTemas > 0 && (
                    <ChipDeEstado tono="neutro" testid="flashcard-chip-available">
                      {t('study:center.count_available', { count: totalFlashcardsDeTemas })}
                    </ChipDeEstado>
                  )}
                  {flashcardSummary && (
                    <>
                      <ChipVerde testid="flashcard-chip-known">
                        {t('study:center.flashcard_chips.known', { known: flashcardSummary.totals.known, total: flashcardSummary.totals.total })}
                      </ChipVerde>
                      <ChipDeEstado tono="naranja" testid="flashcard-chip-learning">
                        {t('study:center.flashcard_chips.learning', { count: flashcardSummary.totals.total - flashcardSummary.totals.known })}
                      </ChipDeEstado>
                    </>
                  )}
                </div>
              )}
              {selectedDocs.length > 1 && flashcardSummary && (
                <div className="flex flex-col items-start" data-testid="flashcard-breakdown">
                  <button type="button" onClick={() => setFcBreakdownOpen(o => !o)}
                    aria-expanded={fcBreakdownOpen}
                    className={`${LIGA_RENGLON} focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded`}
                    data-testid="flashcard-breakdown-toggle">
                    <AreaDe44 />
                    {t(fcBreakdownOpen ? 'study:center.flashcard_chips.breakdown_hide' : 'study:center.flashcard_chips.breakdown_show')}
                    {fcBreakdownOpen ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
                  </button>
                  {fcBreakdownOpen && (
                    <div className="mt-2 w-full rounded-[14px] border border-[#1F1F1F] bg-[#101010] p-3 flex flex-col gap-2.5 max-h-[200px] overflow-y-auto overscroll-contain" data-testid="flashcard-breakdown-list">
                      {selectedDocs.map(id => {
                        const d = flashcardSummary.documents?.[id];
                        if (!d) return null;
                        return (
                          <div key={id} className="min-w-0">
                            <p className="text-[13px] leading-[18px] font-semibold text-[#F4F4F3] truncate" title={d.name} data-testid="fc-summary-lead">{d.name}</p>
                            <p className="text-[12.5px] leading-[18px]">{avanceDeUnDocumento(d.known, d.total)}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* CE6 y CE15 (firma 2): el recuadro de lo que falta, tambien para el invitado. */}
              {recuadroDeFlashcards && (
                <RecuadroDeLoQueFalta
                  testid="missing-flashcards-warning"
                  texto={t('study:center.missing_flashcards.warning', { count: docsMissingFlashcards.length })}
                  textoBoton={t('study:center.missing_flashcards.generate_btn')}
                  trabajando={regenState.active}
                  textoTrabajando={t('study:center.missing_flashcards.generating_btn', { current: regenState.current, total: regenState.total })}
                  onGenerar={handleGenerateMissingFlashcards}
                />
              )}
              {botonDeTemas}
            </>
          )}
          </div>
          {/* El pie, solo tablet y computadora: "{n} documents selected" y el boton de 52. En el
              celular los dos viven en la barra fija de abajo. centro-espacio-2: "{n} documents
              selected" solo en la computadora (`DOCS_ELEGIDOS`); en la tablet, solo el boton y, si
              toca, el aviso de por que esta apagado. TANDA 16 (#196): "Start" corto solo
              de 1024 a 1279; el resto lee la etiqueta larga. FLASH-3 (pieza 1): `starting` se
              suma al MISMO `disabled` de siempre. */}
          <div className={PIE_DE_SETTINGS}>
            <p className={DOCS_ELEGIDOS}>{t('study:center.docs_selected', { count: selectedDocs.length })}</p>
            <Boton onClick={startSession} disabled={startDisabled} trabajando={starting} apagadoSuave={startApagadoSuave}
              alTocarApagado={alTocarStartApagado} alto="barra" aLoAncho icono={Play}
              className="!rounded-[14px] !text-[16px]" testid="start-session-btn">
              <span className="lg:hidden xl:inline">{t('study:center.start_btn')}</span>
              <span className="hidden lg:inline xl:hidden">{t('study:center.start_btn_short')}</span>
            </Boton>
            {!availability.available && (
              <p className="text-[13px] leading-[18px] text-[#9A9A9A] text-center" data-testid="offline-cta-note">
                {availabilityMessage()}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>

    {/* Barra de acción fija (solo celular), FUERA del contenedor animado: un ancestro con
        transform se volvería el bloque contenedor de lo fijo (smoke F6.4a). Armada arriba, antes
        de la puerta del dato, y pintada aquí en la misma posición del fragmento que en la rama de
        carga (TANDA 18, #208). */}
      {barraDeAccion}

      {/* TANDA 29 (#272): el selector de temas. Su cierre (X, fuera, "Done") aplica lo marcado. */}
      <TopicPickerModal
        open={selectorAbierto}
        onClose={() => setSelectorAbierto(false)}
        mode={mode}
        documents={catalogoDeTemas?.documents || []}
        loading={temasCargando}
        cargaFallida={cargaFallida}
        selected={temasElegidos}
        onToggle={alternarTema}
        onClear={limpiarTemas}
      />

      {/* F6.7b: el candado D-G. Solo existe si alguien sin Pro intentó estudiar sin señal. */}
      {proGateVariant && (
        <OfflineProModal
          variant={proGateVariant}
          onClose={() => setProGateVariant(null)}
          onUpgrade={() => {
            const dest = proGateVariant === GATE_VARIANT.GUEST ? '/signup' : '/upgrade';
            setProGateVariant(null);
            navigate(dest);
          }}
        />
      )}
    </>
  );
}
